import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {PanelBaseComponent} from '../panels/panel-base.component';
import {UserSettingsService} from '../user-settings.service';
import {MessageBusService} from '../message-bus.service';
import {checkClipboardPermissions, DetectMethodChanges, isVoid} from '../utils';
import {LastQuoteCacheService} from '../last-quote-cache.service';
import {QuoteDto} from '../shell-communication/dtos/quote-dto.class';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';
import {TradingInstrumentsService} from '../trading-instruments/trading-instruments-service.interface';
import {TradingInstrumentKind} from '../trading-instruments/trading-instrument-kind.enum';
import {UniqueUnderlying} from '../trading-instruments/trading-instruments-service.interface';
import {GenericConfirmationDialogComponent} from '../generic-confirmation-dialog/generic-confirmation-dialog.component';
import {ToastrService} from 'ngx-toastr';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {GetBetasShell, GetBetasShellReply} from '../shell-communication/shell-operations-protocol';
import {
    isMarketProxy, PhaBalanceMode, PhaBalanceResult, PhaExpiration, PhaHeldOption, PhaHeldPosition, PhaNetHedge, PhaOptionType,
    PhaPortfolioContext, PhaPremiumChoice, PhaPremiumRow, PhaPremiumTotals, PhaProtectionRow, PhaProtectionTotals, PhaSheet, PhaSide,
    PhaTotals, PhaUnderlyingRow, PhaUnderlyingAssetType, PhaOptimizationSnapshot, optionPrice
} from './pha-sheet.model';
import {
    CollarsBucketDto, CollarsPositionDto, CollarsTerminalDto, GetCollarsAccessibleTerminals, ListCollarsBuckets, ListCollarsPositions, RecordCollarsAccountCash
} from '../shell-communication/collars-operations';
import {OpenSubjectRequest, PHA_SELECT_PORTFOLIO_REQUEST_TOPIC, SELECT_REQUEST_TTL_MS} from '../action-board/action-board.model';
import {TradingInstrumentDisplayNameService} from '../trading-instruments/trading-instrument-display-name.service';
import {OptionsChainService} from '../option-chains.service';
import {SessionService} from '../authentication/session-service.service';
import {OptionExpirationDescriptor} from '../shell-communication/shell-dto-protocol';
import {makeOptionTicker, OptionType, parseOptionTicker} from '../options-common/options.model';
import {AnalysisSessionService} from './session/analysis-session.service';
import {
    optionContributionsOf, optionHoldingsOf, buildChangeSet, chainName, composeChainText, composeOrderText, PhaBlock, PhaChange, PhaChangeSet, PhaIdRegistry, PhaPendingCounts,
    PhaRowMark, toBlockInputs, tosDate
} from './session/analysis-changes.builder';
import {duplicateContracts, optionMessage} from './pha-contract-validation';
import {nearestExpiration, PHA_OTM_STEPS, sectionSettings} from './pha-section-settings';
import {parseSheetState, PhaOptionRowState, PhaSheetState, serializeSheet} from './session/pha-sheet-state';
import {PhaAddItmCallDialogComponent, PhaItmCallSelection} from './pha-add-itm-call-dialog.component';
import {PhaHedgeProductsDialogComponent} from './pha-hedge-products-dialog.component';
import {PhaBenchmarkDialogComponent} from './pha-benchmark-dialog.component';
import {PhaExportBlock, PhaExportDialogComponent, PhaExportGroup} from './pha-export-dialog.component';
import {AnalysisSection, AnalysisSessionDto} from '../shell-communication/analysis-session-operations';
import {SettlementPriceService} from '../hedging-grid/settlement-price.service';
import {TimestampsService} from '../timestamps.service';
import {orderOptionTrades, PhaOptionRow, PhaOptionSection, PhaOptionStrategyType} from './pha-option-strategy';
import {calculateRounding, PhaRoundingReport} from './pha-rounding-optimizer';

export interface OptimizationUndoCheckpoint {
    before: PhaOptimizationSnapshot;
    afterFingerprint: string;
    brokerFingerprint: string;
    label: string;
    previousReport: PhaRoundingReport | null;
}

// THE PORTFOLIO HEDGING ANALYSIS PANEL (CR-20260929-001; the analysis SESSION, CR-20260930-001 M2).
//
// A design surface for a protected stock position (a collar in the broad sense), sitting NEXT TO the
// "Portfolio Hedging" (Collars) panel. Since CR-20260930-001 the design lives in a backend-owned
// ANALYSIS SESSION: import a combo (the before state), design the after state (yellow split cells),
// EXPORT order blocks to ToS one copy at a time (the rows lock), REVOKE a copy, and let Collars
// acquire the fills. Nothing is acquired here (the hard split, system description §3).
//
// LAYOUT (owner sketch + rulings R4/R5, 2026-09-29):
//   sticky panel header   — account size ("cash"), free cash, the session counts, Reset, Copy All, zoom
//   three sub-sections    — Underlyings / Premium / Protection, vertical as-split areas (the SAME
//                           mechanism as Collars and the Session History — NOT the Hedging Grid's
//                           collapsible sections), each with its own header (with its counts + Copy) and body
//   sticky Net Hedge      — the one number the whole exercise is about; always visible
//
// SESSION STATE lives in the Shell (§5 "Durability"): the sheet is sent after every change and comes
// back on restore (combo switch and back, panel reopen, login). `getState` still returns null: the
// panel's OWN user setting stays empty; the session is the record, keyed by combo.

export type PhaSection = 'underlyings' | 'premium' | 'protection';

interface SectionSizes {
    underlyings: number;
    premium: number;
    protection: number;
}

const DEFAULT_SECTION_SIZES: SectionSizes = {underlyings: 40, premium: 30, protection: 30};

// Owner ruling 2026-09-29: zoom OUT only, 30%..100%. Never above 100%.
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 1.0;
const ZOOM_STEP = 0.1;

// A price older than this is shown stale ([my call]: 15 minutes, re-checked every 30 seconds).
const STALE_AFTER_MS = 15 * 60 * 1000;
const STALE_CHECK_MS = 30 * 1000;

const EMPTY_COUNTS: PhaPendingCounts = {notExported: 0, notAcquired: 0, overdue: 0};

@Component({
    selector: 'ets-portfolio-hedging-analysis-panel',
    templateUrl: './portfolio-hedging-analysis-panel.component.html',
    styleUrls: ['./portfolio-hedging-analysis-panel.component.scss'],
    // The class MUST be bound on the HOST (the Maven/Collars lesson): hiding only the inner
    // container leaves the host at height:100% and pushes a later-added sibling panel out of view.
    host: {'[class.ets-hidden]': '!isActive'},
    // One session service PER PANEL (never on the module: it would hoist to the root injector).
    providers: [AnalysisSessionService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortfolioHedgingAnalysisPanelComponent extends PanelBaseComponent {

    constructor(
        changeDetector: ChangeDetectorRef,
        userSettingsService: UserSettingsService,
        messageBus: MessageBusService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _toastr: ToastrService,
        private readonly _shellClient: ShellClientService,
        private readonly _chains: OptionsChainService,
        private readonly _displayNames: TradingInstrumentDisplayNameService,
        private readonly _session: SessionService,
        private readonly _analysis: AnalysisSessionService,
        private readonly _settlementPrices: SettlementPriceService,
        private readonly _timestamps: TimestampsService,
        private readonly _instruments: TradingInstrumentsService = null
    ) {
        super(changeDetector, userSettingsService, messageBus);
    }

    private readonly _unsub = new Subject<void>();

    @ViewChild('confd', {static: false}) confirmationDialog: GenericConfirmationDialogComponent;
    @ViewChild(PhaAddItmCallDialogComponent, {static: false}) itmCallDialog: PhaAddItmCallDialogComponent;
    @ViewChild(PhaHedgeProductsDialogComponent, {static: false}) hedgeProductsDialog: PhaHedgeProductsDialogComponent;
    @ViewChild(PhaBenchmarkDialogComponent, {static: false}) benchmarkDialog: PhaBenchmarkDialogComponent;

    get benchmarkDesignKey(): string {
        return this.benchmarkDialog?.visible
            ? this._sourceVersion + '|' + this.optimizationFingerprint() + '|' + this.optimizationBrokerFingerprint() : '';
    }

    @DetectMethodChanges()
    onBenchmarkClicked(): void {
        if (this.isUnlocked && !this.optimizing && !this.panelLoading && !this.resetting) { this.benchmarkDialog.open(); }
    }

    @DetectMethodChanges()
    onHedgeProductsClicked(): void {
        if (this.isUnlocked && !this.optimizing) { this.hedgeProductsDialog.open(); }
    }
    @ViewChild(PhaExportDialogComponent, {static: false}) exportDialog: PhaExportDialogComponent;

    // ---- the analysis --------------------------------------------------------------------------
    // ONE sheet for the panel's lifetime (system-description.md §4 "The analysis").
    readonly sheet = new PhaSheet(ticker => this._chains.isOptionExpired(ticker),
        ticker => this._displayNames.getDisplayNameForTicker(ticker),
        ticker => this._instruments?.getInstrumentByTicker(ticker) ? this.underlyingType(ticker) : null,
        ticker => this._instruments?.getInstrumentByTicker(ticker)?.pointValue);

    private underlyingType(ticker: string, savedType?: string): PhaUnderlyingAssetType {
        const kind = this._instruments?.getInstrumentByTicker(ticker)?.kind;
        if (kind != null) {
            return kind === TradingInstrumentKind.Futures || String(kind) === 'Futures' ? 'Future' : 'Stock';
        }
        return savedType === 'Future' ? 'Future' : 'Stock';
    }

    // ---- the gate ----------------------------------------------------------------------------
    // Account value is current stock value plus cash. A selected combo is authoritative.
    get accountValue(): number | null {
        if (this.selectedComboId) {
            const combo = this.selectedComboChoice;
            return combo && combo.hasStartingCapital ? this.sheet.currentAccountValue : null;
        }
        return this.sheet.currentAccountValue;
    }

    get isUnlocked(): boolean {
        if (!this.sourceReady) { return false; }
        if (this.selectedComboId) {
            const combo = this.selectedComboChoice;
            return !!combo && combo.hasStartingCapital && this.sheet.isUnlocked;
        }
        return this.sheet.isUnlocked;
    }

    /** Used by action refusals; the entry instruction is displayed over the subsections. */
    get accountGateHint(): string {
        if (!this.selectedComboId) {
            return 'Select a portfolio and combo, or enter an account value to start.';
        }
        const combo = this.selectedComboChoice;
        if (!combo) {
            return 'Loading combo account value...';
        }
        return combo.hasStartingCapital
            ? 'Enter an account value to start'
            : 'Set starting capital in Portfolio Hedging to unlock this combo';
    }

    async onAccountValueChanged(ev: {value: number}): Promise<void> {
        if (this.optimizing || !this.sourceReady || this.selectedComboId || this._restoreDepth) { return; }
        if ((isVoid(ev) ? null : ev.value) === this.accountValue) { return; }
        const version = ++this._sourceVersion;
        this.sheet.setAccountValue(isVoid(ev) ? null : ev.value);
        if (this.sheet.isUnlocked && !this._analysis.isLive) { await this.startHomelessSession(); }
        if (version !== this._sourceVersion) { return; }
        this.publishSheet();
    }

    onStockAllocationPercentChanged(ev: {value: number}): void {
        if (this.optimizing || !this.isUnlocked) {
            return;
        }
        this.sheet.setStockAllocationPercent(ev && typeof ev.value === 'number' ? ev.value : 100);
        this.publishSheet();
    }

    get maxStockValue(): number | null {
        return this.sheet.maxStockValue;
    }

    /** Free cash: account value minus the underlyings' current market value. */
    get freeCash(): number | null {
        const combo = this.allCombos.find(c => c.comboId === this.selectedComboId);
        if (combo && combo.hasStartingCapital) { return combo.freeCash; }
        return this.sheet.totals.freeCash;
    }

    // ---- the session (CR-20260930-001 M2) ---------------------------------------------------------
    // The Shell owns the session; the panel derives the change set (blocks, marks, counts) from the
    // sheet as displayed + the session's before snapshot + its locked blocks, after every change to
    // either, and sends the design (sheet state + blocks) whenever the DESIGN changed — never on a
    // price tick alone (the fingerprint below tells them apart).
    // PER SESSION (M2 smoke: a block id reused from an aborted session violated the Shell's block key —
    // the ids are the session's, never the panel's): replaced whenever the session id changes.
    private _ids = new PhaIdRegistry();
    private _idsSessionId: string | null = null;
    changeSet: PhaChangeSet = {blocks: [], marks: {}, counts: {total: EMPTY_COUNTS, Underlyings: EMPTY_COUNTS, Premium: EMPTY_COUNTS, Protection: EMPTY_COUNTS}};
    marks: {[rowId: number]: PhaRowMark} = {};
    private _lastDesignFingerprint = '';
    private _restoreDepth = 0;
    contractEditRevision = 0;
    private _sourceVersion = 0;
    private _sourceLoadState: 'loading' | 'ready' | 'failed' = 'ready';
    private _readySessionId: string | null | undefined;
    private _publishingSheet = 0;
    private _portfolioLoadFailed = false;

    get loadFailed(): boolean { return this._sourceLoadState === 'failed' || !!this._portfolioLoadFailed; }
    get sourceReady(): boolean {
        return (!this._sourceLoadState || this._sourceLoadState === 'ready') && !this._portfolioLoadFailed;
    }

    async onRetryLoad(): Promise<void> {
        if (this.panelLoading || this.resetting || this.optimizing || !this.loadFailed) { return; }
        if (this._portfolioLoadFailed && !await this.loadPortfolios()) { return; }
        if (this.selectedComboId) { await this.selectCombo(this.selectedComboId, true); }
        else { await this.restoreHomelessIfAny(); }
    }

    get sessionLive(): boolean {
        return this._analysis.isLive;
    }

    get sessionHomeless(): boolean {
        return this._analysis.isHomeless;
    }

    /** The short session id shown in the header (the trace key of every log line, §8). */
    get sessionLabel(): string {
        const id = this._analysis.sessionId;
        return id ? id.substring(0, 8) : '';
    }

    get totalCounts(): PhaPendingCounts {
        return this.changeSet.counts.total;
    }

    countsOf(section: PhaSection): PhaPendingCounts {
        return this.changeSet.counts[sectionKeyOf(section)];
    }

    /** "2 to Copy · 1 Awaiting Fill (1 Overdue)" — the two debts (§6.8). Empty when nothing pends. */
    countsText(c: PhaPendingCounts): string {
        if (!c || (!c.notExported && !c.notAcquired)) {
            return '';
        }
        const parts: string[] = [];
        if (c.notExported) {
            parts.push(c.notExported + ' to Export');
        }
        if (c.notAcquired) {
            parts.push(c.notAcquired + ' Awaiting Fill' + (c.overdue ? ' (' + c.overdue + ' Overdue)' : ''));
        }
        return parts.join(' · ');
    }

    /** Re-derive blocks, marks and counts; send the design when it changed. */
    private refreshChangeSet(): void {
        if (this.sheet.contractConflict) {
            this.contractEditRevision = (this.contractEditRevision || 0) + 1;
            this._toastr.warning(this.sheet.contractConflict);
            this.sheet.contractConflict = null;
        }
        const holdings = optionHoldingsOf(this._analysis.snapshot, this._analysis.blocks);
        this.sheet.reconcileOptionHoldings(holdings, optionContributionsOf(this.sheet, this._analysis.snapshot, this._analysis.blocks));
        this.sheet.setOptionHoldings(holdings);
        this.premiumTotals = {...this.sheet.premiumTotals};
        this.protectionTotals = {...this.sheet.protectionTotals};
        this.netHedge = {...this.sheet.netHedge};
        this.premiumRows = orderOptionTrades(this.sheet.premiumRows);
        this.protectionRows = orderOptionTrades(this.sheet.protectionRows);
        this.changeSet = buildChangeSet(this.sheet, this._analysis.snapshot, this._analysis.blocks, this._ids);
        this.marks = {...this.changeSet.marks};
        // The rows of a copied block are frozen for the sheet's own moves (balance, OTM movers, pool clamps).
        const locked: {[id: number]: true} = {};
        Object.keys(this.changeSet.marks).forEach(id => {
            if (this.changeSet.marks[+id].locked) {
                locked[+id] = true;
            }
        });
        this.sheet.sessionLockedRowIds = locked;

        this.saveReadyDesign();
        this.refreshExportItems();
    }

    private saveReadyDesign(): void {
        if (this.sourceReady && !this._publishingSheet && this._analysis.isLive && !this._restoreDepth
            && (this._readySessionId === undefined || this._readySessionId === this._analysis.sessionId)) {
            const blocks = toBlockInputs(this.changeSet.blocks, this._ids);
            const sheetStateJson = JSON.stringify(serializeSheet(this.sheet, {
                premium: {targetDte: this.targetDte.premium, otmStep: this.otmStep.premium},
                protection: {targetDte: this.targetDte.protection, otmStep: this.otmStep.protection}
            }));
            const fingerprint = JSON.stringify(blocks) + '|' + sheetStateJson;
            if (fingerprint !== this._lastDesignFingerprint) {
                this._lastDesignFingerprint = fingerprint;
                this._analysis.scheduleReplace(sheetStateJson, blocks);
            }
        }
    }

    /** The session was re-read (a push, an op): its blocks may have changed status — repaint. */
    private onSessionChanged(dto: AnalysisSessionDto | null): void {
        this.checkOptimizationUndo();
        if (dto) {
            if (dto.sessionId !== this._idsSessionId) {
                this._ids = new PhaIdRegistry();
                this._idsSessionId = dto.sessionId;
                this._lastDesignFingerprint = '';
            }
            this._ids.seed(dto.blocks);
        }
        this.refreshChangeSet();
        this._changeDetector.detectChanges();
        if (dto && dto.status !== 'Live') {
            // Finished (every block acquired) or aborted elsewhere: the combo is free again.
            console.info('[pha] session ' + dto.sessionId + ' ended: ' + dto.status + ' (' + dto.endReason + ')');
        }
    }

    /** The last session id whose end was handled: the push and a re-read can both discover it — first wins. */
    private _lastEndedHandled: string | null = null;

    /** The session ended by a push or a re-read: Finished → toast + re-import the combo into a fresh session. */
    private async onSessionEnded(ended: {sessionId: string; status: string; reason: string}): Promise<void> {
        console.info('[pha] session ' + ended.sessionId + ' ended: ' + ended.status + ' (' + ended.reason + ')');
        if (ended.status !== 'Finished' && ended.status !== 'Ended') {
            return;   // Aborted elsewhere changes nothing (owner ruling): the aborting side reloads itself.
        }
        if (this._lastEndedHandled === ended.sessionId) {
            return;
        }
        this._lastEndedHandled = ended.sessionId;
        if (ended.status === 'Finished') {
            this._toastr.success('The Analysis Is Finished: Every Exported Order Was Applied in Portfolio Hedging');
        } else {
            // 'Ended': discovered gone on a re-read (a lost push, or an end while ESS was down). The
            // design is dead either way — load the combo as the book stands, the same road as a Reset.
            this._toastr.info('The Analysis Session Ended Elsewhere; the Combo Is Reloaded as the Book Stands');
        }
        if (this.selectedComboId) {
            await this.selectCombo(this.selectedComboId, true);
        }
    }

    // ---- Reset (owner, 2026-09-29 vrequest, item 8; CR-20260930-001 §6.6) -------------------------
    // Reset ABORTS the session: unexported changes are dropped. When exported blocks are still
    // waiting for their fills the trader chooses: CLEAR them (never placed: gone from Collars too)
    // or LEAVE THEM ALONE (presumed placed: they stay in Collars, flagged with this session's id).
    // Then the combo is loaded again from the Collars book into a NEW session (= the state right
    // after the load); account value, selectors and zoom stay.
    resetChoiceVisible = false;

    async onResetClicked(): Promise<void> {
        if (this.optimizing || !this.isUnlocked) {
            return;
        }
        const exported = this.changeSet.counts.total.notAcquired;
        if (this._analysis.isLive && exported > 0) {
            this.resetChoiceVisible = true;   // the two-way warning (§6.6)
            this._changeDetector.detectChanges();
            return;
        }
        try {
            await this.confirmationDialog.show(['Discard every change and reload the analysis?']);
        } catch (e) {
            return;
        }
        await this.performReset('clear');
    }

    @DetectMethodChanges()
    onResetChoiceCancelled(): void {
        this.resetChoiceVisible = false;
    }

    async onResetChoice(choice: 'clear' | 'leave'): Promise<void> {
        this.resetChoiceVisible = false;
        this._changeDetector.detectChanges();
        await this.performReset(choice);
    }

    /**
     * True while a Reset runs: the abort, then the reload of the combo (session, positions, every
     * option chain). The panel shows a loading indicator over itself and Reset is disabled (owner
     * 2026-09-30: "Reset must show a loading indicator until things are done").
     */
    resetting = false;

    private async performReset(choice: 'clear' | 'leave'): Promise<void> {
        if (this.resetting) {
            return;
        }
        this.resetting = true;
        const comboId = this.selectedComboId;
        const sourceVersion = this._sourceVersion;
        try {
            this._changeDetector.detectChanges();
            this.cancelOtmMovers();
            if (this._analysis.isLive) {
                const ok = await this._analysis.reset(choice);
                if (!ok) {
                    this._toastr.error('The Analysis Could Not Be Reset');
                    return;
                }
                console.info('[pha] session reset (' + choice + ')');
            }
            if (sourceVersion !== this._sourceVersion || comboId !== this.selectedComboId) { return; }
            if (this.selectedComboId) {
                console.info('[pha] reset: reloading combo ' + this.selectedComboId);
                if (!await this.selectCombo(this.selectedComboId, true)) { return; }
            } else {
                console.info('[pha] reset: no combo selected, sheet emptied');
                this._analysis.detach();
                this.replaceSheet(null, []);
                this._readySessionId = undefined;
                this.publishSheet();
            }
            this._toastr.success(choice === 'leave'
                ? 'The Analysis Was Reset; Its Exported Orders Stay in Portfolio Hedging, Waiting for Their Fills'
                : 'The Analysis Was Reset');
        } catch (error) {
            if (comboId === this.selectedComboId) { this._sourceLoadState = 'failed'; }
            console.error('[pha] analysis reset failed', error);
            this._toastr.error('The Analysis Could Not Be Reset');
        } finally {
            this.resetting = false;
            this._changeDetector.detectChanges();
        }
    }

    // ---- the Underlyings table (a custom row grid, hedge-pad style; owner ruling 2026-09-29) ----
    // The table is OnPush and dumb: after every change to the sheet the panel hands it a NEW rows
    // array and a NEW totals object (the sheet mutates rows in place; a fresh reference is what
    // makes the child repaint).
    underlyingRows: PhaUnderlyingRow[] = [];
    underlyingTotals: PhaTotals = null;
    premiumRows: PhaPremiumRow[] = [];
    premiumTotals: PhaPremiumTotals = null;
    protectionRows: PhaProtectionRow[] = [];
    protectionTotals: PhaProtectionTotals = null;
    protectionChoices: Array<{symbol: string, displayName: string}> = [];
    premiumChoices: PhaPremiumChoice[] = [];
    netHedge: PhaNetHedge = {premium: 0, protection: 0, net: 0};

    /** Underlying A-Z first, then strike descending within each underlying. */
    private publishSheet(): void {
        this._publishingSheet = (this._publishingSheet || 0) + 1;
        try {
            // All linked contracts change together; rebuild their identities before deriving orders.
            this.sheet.premiumRows.filter(r => r.strategyId && !r.crossedOut).forEach(r => this.buildOptionTicker(r));
            this.sheet.protectionRows.filter(r => r.strategyId && !r.crossedOut).forEach(r => this.buildProtectionTicker(r));
            this.refreshChangeSet();
            // Display stocks before options without changing sheet/storage or order-block ordering.
            this.checkOptimizationUndo();
            this.underlyingRows = this.sheet.rows.filter(r => r.assetType !== 'Call')
                .concat(this.sheet.rows.filter(r => r.assetType === 'Call'));
            this.underlyingTotals = {...this.sheet.totals};
            this.premiumRows = orderOptionTrades(this.sheet.premiumRows);
            this.premiumTotals = {...this.sheet.premiumTotals};
            this.protectionRows = orderOptionTrades(this.sheet.protectionRows);
            this.protectionTotals = {...this.sheet.protectionTotals};
            this.protectionChoices = this.sheet.protectionChoices();
            this.premiumChoices = this.sheet.premiumChoices();
            this.netHedge = {...this.sheet.netHedge};
            this.reconcileOptionSubscriptions();
            this.reconcileSettlementPrices();
            this.reconcileSpotSubscriptions();
            this.logStateChanges();
            this._changeDetector.detectChanges();
        } finally { --this._publishingSheet; }
        // Publication must finish before its design becomes eligible for persistence.
        this.saveReadyDesign();
    }

    // ---- adding an underlying (symbol picker) ------------------------------------------------
    symbolPickerVisible = false;
    readonly underlyingAddItems = [{id: 'underlying', text: 'Stock'}, {id: 'call', text: 'ITM Call'}];
    selectedUnderlyingAddType: 'underlying' | 'call' = 'underlying';

    get underlyingAddText(): string {
        return this.selectedUnderlyingAddType === 'call' ? 'ITM Call' : 'Stock';
    }

    @DetectMethodChanges()
    onUnderlyingAddItemClicked(ev: {itemData: {id: string}}): void {
        if (!this.isUnlocked || !ev || !ev.itemData) { return; }
        if (ev.itemData.id === 'call' || ev.itemData.id === 'underlying') {
            this.selectedUnderlyingAddType = ev.itemData.id;
        }
    }

    @DetectMethodChanges()
    onUnderlyingAddButtonClicked(): void {
        if (this.optimizing || !this.isUnlocked) { return; }
        if (this.selectedUnderlyingAddType === 'call') { this.itmCallDialog.open(); }
        else { this.openSymbolPicker(); }
    }

    private _addingItmCall = false;
    @DetectMethodChanges({isAsync: true})
    async onItmCallAdded(selection: PhaItmCallSelection): Promise<void> {
        if (this._addingItmCall || !this.isUnlocked || !this.itmCallDialog.canAdd) { return; }
        this._addingItmCall = true;
        const comboId = this.selectedComboId;
        try {
            if (this.sheet.hasTicker(selection.optionTicker)) {
                this._toastr.info('This call is already on the sheet');
                return;
            }
            if (!comboId && !this._analysis.isLive) { await this.startHomelessSession(); }
            if (this.selectedComboId !== comboId || !this.isUnlocked || !this.itmCallDialog.matches(selection)) { return; }
            const quote = this._lastQuoteCache.getLastQuote(selection.instrument.ticker);
            if (!quote || !(quote.lastPx > selection.strike)) { return; }
            const row = this.sheet.addCallUnderlying(selection.optionTicker, selection.instrument.ticker, selection.strike,
                selection.expiration.optionExpirationDate, selection.expiration.expirationTicker, selection.contracts);
            if (!row) { this.publishSheet(); return; }
            this.sheet.setProtectionSpot(selection.instrument.ticker, quote.lastPx);
            this.subscribePrice(row.ticker);
            this.itmCallDialog.close();
            this.publishSheet();
            this.fetchBeta(selection.instrument.ticker, row.ticker);
        } finally { this._addingItmCall = false; }
    }

    @DetectMethodChanges()
    openSymbolPicker(): void {
        if (this.optimizing || !this.isUnlocked) {
            return;
        }
        this.symbolPickerVisible = true;
    }

    @DetectMethodChanges()
    onSymbolPickerClosed(): void {
        this.symbolPickerVisible = false;
    }

    async onInstrumentPicked(picked: TradingInstrument | UniqueUnderlying): Promise<void> {
        this.symbolPickerVisible = false;
        if (this.optimizing || !this.isUnlocked) {
            this._toastr.info(this.accountGateHint);
            this._changeDetector.detectChanges();
            return;
        }
        if (!picked) {
            this._changeDetector.detectChanges();
            return;
        }
        // The dialog hands back a TradingInstrument (ticker, numeric kind) in instrument mode and a
        // UniqueUnderlying (underlying, kind as text) in underlyings mode; accept both.
        const asTi = picked as TradingInstrument;
        const asUl = picked as UniqueUnderlying;
        const ticker: string = asTi.ticker || asUl.underlying;
        const kindName: string = typeof picked.kind === 'number' ? TradingInstrumentKind[picked.kind] : String(picked.kind || '');
        const displayName: string = picked.displayName || ticker;
        if (!ticker) {
            this._changeDetector.detectChanges();
            return;
        }
        // v1: a stock or ETF only (system-description.md section 4 "The Underlyings section").
        // Futures, options, indexes and a short underlying are out of v1.
        if (kindName !== TradingInstrumentKind[TradingInstrumentKind.Stock]) {
            this._toastr.warning(`${displayName}: Only a Stock or ETF Can Be an Underlying in This Version`);
            this._changeDetector.detectChanges();
            return;
        }
        if (this.sheet.hasTicker(ticker)) {
            this._toastr.info(`${displayName} Is Already on the Sheet`);
            this._changeDetector.detectChanges();
            return;
        }
        // A FRESH START (§6.1): the first underlying with no combo selected opens a homeless session.
        if (!this.selectedComboId && !this._analysis.isLive) {
            await this.startHomelessSession();
        }
        this.sheet.addUnderlying(ticker, displayName);
        this.subscribePrice(ticker);
        this.publishSheet();
        this.fetchBeta(ticker);
    }

    private async startHomelessSession(): Promise<void> {
        const version = this._sourceVersion;
        try {
            const dto = await this._analysis.startOrRestore(null);
            if (version !== this._sourceVersion) { return; }
            if (!dto) { throw new Error('The analysis session could not be started'); }
            this._readySessionId = dto.sessionId;
            console.info('[pha] homeless session ' + dto.sessionId + ' ' + (dto.blocks && dto.blocks.length ? 'restored' : 'started'));
        } catch (error) {
            if (version !== this._sourceVersion) { return; }
            this._sourceLoadState = 'failed';
            console.error('[pha] homeless session could not be started', error);
            this._toastr.warning('The Analysis Session Could Not Be Started; the Design Will Not Be Saved');
        }
    }

    // ---- beta (M2): one external source, EODHD, through the Shell -------------------------------
    // Fetched when the ticker is added (section 4 "Beta"); blank on failure, and beta optimization
    // then refuses naming the row. Never calculated here.
    private async fetchBeta(ticker: string, rowTicker: string = ticker): Promise<void> {
        // The market itself has beta 1 by definition (owner ruling 2026-09-29): no lookup.
        if (isMarketProxy(ticker)) {
            this.sheet.setBeta(rowTicker, 1);
            this.publishSheet();
            return;
        }
        let reply: GetBetasShellReply;
        try {
            reply = await this._shellClient.processQuery<GetBetasShellReply>(new GetBetasShell([ticker]));
        } catch (error) {
            console.error('[pha] GetBetasShell FAILED for ' + ticker, error);
            this._toastr.warning(`Beta for ${ticker} Could Not Be Read`);
            return;
        }
        const row = (reply && reply.betas || []).find(b => b.ticker === ticker);
        const beta = row && typeof row.beta === 'number' && isFinite(row.beta) ? row.beta : null;
        if (beta === null) {
            const why = (row && row.message) || (reply && reply.message) || 'no beta from the source';
            console.warn('[pha] no beta for ' + ticker + ': ' + why);
        }
        if (!this.sheet.hasTicker(rowTicker)) {
            return; // removed while the lookup was in flight
        }
        this.sheet.setBeta(rowTicker, beta);
        this.publishSheet();
    }

    // ---- live prices -------------------------------------------------------------------------
    private subscribePrice(ticker: string): void {
        if (parseOptionTicker(ticker) && this._chains.isOptionExpired(ticker)) { return; }
        const q = this._lastQuoteCache.subscribeTicker(ticker);
        if (q) {
            this.applyQuote(q);
        }
    }

    private unsubscribePrice(ticker: string): void {
        this._lastQuoteCache.unsubscribeTicker(ticker);
    }

    private onQuotes(quotes: QuoteDto[]): void {
        // NOT gated on the Underlyings table: a protection row on SPX prices with no underlying at
        // all (the M4 smoke found this guard dropping every quote on an empty sheet).
        if (!quotes || !quotes.length) {
            return;
        }
        let touched = false;
        quotes.forEach(q => {
            if (this.sheet.hasTicker(q.ticker)) {
                touched = this.applyQuote(q) || touched;
            }
            if (this._optionSubscriptions[q.ticker]) {
                if (!(q.bid >= 0 && q.ask > 0) && !(q.mid > 0) && !(q.lastPx > 0)) {
                    console.warn('[pha] unpriced option quote ' + q.ticker + ' bid=' + q.bid + ' ask=' + q.ask + ' mid=' + q.mid + ' last=' + q.lastPx);
                }
                touched = this.sheet.setOptionQuote(q.ticker, finiteOrNull(q.bid), finiteOrNull(q.ask), finiteOrNull(q.mid), finiteOrNull(q.lastPx)) || touched;
                touched = this.sheet.setProtectionQuote(q.ticker, finiteOrNull(q.bid), finiteOrNull(q.ask), finiteOrNull(q.mid), finiteOrNull(q.lastPx)) || touched;
            }
            if (this._spotSubscriptions[q.ticker]) {
                const px = pickPrice(q);
                if (px !== null) {
                    touched = this.sheet.setProtectionSpot(q.ticker, px) || touched;
                }
            }
        });
        if (touched) {
            this.retryTwoForOneInitialization();
            this.publishSheet();
        }
    }

    private readonly _twoForOnePending: {[id: number]: {signature: string, next: number, loading: boolean}} = {};

    private twoForOneSignature(row: PhaPremiumRow): string {
        return JSON.stringify([this._sourceVersion, row.ticker, row.strategyId,
            this.sheet.strategyRows('premium', row.id).map(r => [r.id, r.expiration, r.strike,
                r.expectedStrike, r.strategyGap, r.side, (r as PhaPremiumRow).contractsSelling])]);
    }

    private retryTwoForOneInitialization(): void {
        Object.keys(this._twoForOnePending || {}).forEach(id => {
            const pending = this._twoForOnePending[id];
            const row = this.sheet.premiumRows.find(r => r.id === +id);
            if (!row || row.crossedOut || this.sheet.isRowDisabled(row.id) || pending.signature !== this.twoForOneSignature(row)) {
                delete this._twoForOnePending[id]; return;
            }
            if (!pending.loading && (Date.now() >= pending.next || row.expiration && this.sheet.spotOf(row.ticker) > 0)) {
                if (!row.expiration) { delete this._chainByTicker[row.ticker]; }
                this.initializeOption(row, 'premium');
            }
        });
    }

    // ---- option contracts: subscribe exactly the selected contracts, never a chain --------------
    private _optionSubscriptions: {[ticker: string]: true} = {};

    private reconcileOptionSubscriptions(): void {
        const wanted: {[t: string]: true} = {};
        this.sheet.premiumRows.forEach(p => {
            if (p.optionTicker && !this.sheet.isExpired(p)) {
                wanted[p.optionTicker] = true;
            }
        });
        this.sheet.protectionRows.forEach(p => {
            if (p.optionTicker && !this.sheet.isExpired(p)) {
                wanted[p.optionTicker] = true;
            }
        });
        Object.keys(this._optionSubscriptions).forEach(t => {
            if (!wanted[t]) {
                this._lastQuoteCache.unsubscribeTicker(t);
                delete this._optionSubscriptions[t];
            }
        });
        Object.keys(wanted).forEach(t => {
            let q;
            if (!this._optionSubscriptions[t]) {
                this._optionSubscriptions[t] = true;
                q = this._lastQuoteCache.subscribeTicker(t);
            } else {
                // Gap edits clear row quotes, even when a sibling keeps the same contract.
                q = this._lastQuoteCache.getLastQuote(t);
            }
            if (q) {
                this.sheet.setOptionQuote(t, finiteOrNull(q.bid), finiteOrNull(q.ask), finiteOrNull(q.mid), finiteOrNull(q.lastPx));
                this.sheet.setProtectionQuote(t, finiteOrNull(q.bid), finiteOrNull(q.ask), finiteOrNull(q.mid), finiteOrNull(q.lastPx));
            }
        });
    }

    // ---- spot for instruments that are not held: protection instruments (SPX, XSP, SPY) and index
    // Premium rows (owner, 2026-09-29) ----------------------------------------------------------
    private _spotSubscriptions: {[ticker: string]: true} = {};

    private reconcileSpotSubscriptions(): void {
        const wanted: {[t: string]: true} = {};
        this.sheet.rows.forEach(r => {
            if (r.callUnderlying && this.sheet.needsExternalSpot(r.callUnderlying)) { wanted[r.callUnderlying] = true; }
        });
        this.sheet.protectionRows.forEach(p => {
            if (this.sheet.needsExternalSpot(p.symbol)) {
                wanted[p.symbol] = true;
            }
        });
        this.sheet.premiumRows.forEach(p => {
            if (!p.pooled && this.sheet.needsExternalSpot(p.ticker)) {
                wanted[p.ticker] = true;
            }
        });
        Object.keys(this._spotSubscriptions).forEach(t => {
            if (!wanted[t]) {
                this._lastQuoteCache.unsubscribeTicker(t);
                delete this._spotSubscriptions[t];
            }
        });
        Object.keys(wanted).forEach(t => {
            if (!this._spotSubscriptions[t]) {
                this._spotSubscriptions[t] = true;
                const q = this._lastQuoteCache.subscribeTicker(t);
                const px = pickPrice(q);
                if (px !== null) {
                    this.sheet.setProtectionSpot(t, px);
                }
            }
        });
    }

    // ---- Protection (M4) ---------------------------------------------------------------------
    readonly optionAddItems = ['Single Option', 'Butterfly', 'Vertical'].map(text => ({id: text, text}));
    readonly premiumAddItems = [...this.optionAddItems, {id: 'Two-for-One', text: 'Two-for-One'}];
    selectedOptionAddType: {[section: string]: PhaOptionStrategyType} = {premium: 'Single Option', protection: 'Single Option'};
    showGapRows = {premium: true, protection: true};

    @DetectMethodChanges()
    onOptionAddItemClicked(section: PhaOptionSection, ev: {itemData: {id: PhaOptionStrategyType}}): void {
        const items = section === 'premium' ? this.premiumAddItems : this.optionAddItems;
        if (ev && ev.itemData && items.some(i => i.id === ev.itemData.id)) {
            this.selectedOptionAddType[section] = ev.itemData.id;
        }
    }

    @DetectMethodChanges()
    onShowGapRowsChanged(section: PhaOptionSection, ev: {value: boolean}): void {
        this.showGapRows[section] = !!ev.value;
    }

    /** New structures resolve their initial gaps from adjacent listed strikes. */
    private defaultStrategyGaps(symbol: string): number[] {
        return [];
    }

    @DetectMethodChanges()
    onStrategyGapPicked(section: PhaOptionSection, ev: {row: PhaOptionRow, gap: number}): void {
        if (!this.isUnlocked || this.refuseIfLocked(ev.row.id)) { return; }
        const group = this.sheet.setStrategyGap(section, ev.row.id, ev.gap);
        if (!group.length) { this.publishSheet(); return; }
        this.publishSheet();
    }

    @DetectMethodChanges()
    onNewProtectionClicked(): void {
        if (this.optimizing || !this.isUnlocked) {
            return;
        }
        // On the list's first instrument (SPX); the trader re-points it from the row's selector.
        const first = this.sheet.protectionChoices()[0];
        const p = this.sheet.addProtectionRow(first.symbol, first.displayName);
        const type = this.selectedOptionAddType && this.selectedOptionAddType.protection || 'Single Option';
        if (type !== 'Single Option') { this.sheet.linkOptionStrategy('protection', p.id, type, this.defaultStrategyGaps(first.symbol)); }
        this.initializeOption(p, 'protection');
        this.publishSheet();
    }

    onProtectionSymbolPicked(ev: {row: PhaProtectionRow, symbol: string}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        const c = this.sheet.protectionChoices().find(x => x.symbol === ev.symbol);
        const group = this.sheet.strategyRows('protection', ev.row.id);
        if (group.some(r => r.origin === 'held' || r.crossedOut || this.sheet.isRowDisabled(r.id))) { return; }
        const changed = this.sheet.repointOptionStrategy('protection', ev.row.id, ev.symbol, c ? c.displayName : ev.symbol);
        if (changed.length) { this.initializeOption(changed[0], 'protection'); }
        this.publishSheet();
    }

    onProtectionExpirationPicked(ev: {row: PhaProtectionRow, expiration: PhaExpiration | null}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this._optionRequests[ev.row.id] = ++this._optionRequest;
        const p = this.sheet.setProtectionExpiration(ev.row.id, ev.expiration);
        this.buildProtectionTicker(p);
        this.publishSheet();
    }

    onProtectionStrikePicked(ev: {row: PhaProtectionRow, strike: number | null}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this._optionRequests[ev.row.id] = ++this._optionRequest;
        const p = this.sheet.setProtectionStrike(ev.row.id, ev.strike);
        this.buildProtectionTicker(p);
        this.publishSheet();
    }

    onProtectionContractsTyped(ev: {row: PhaProtectionRow, contracts: number}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this.sheet.setProtectionContracts(ev.row.id, ev.contracts);
        this.publishSheet();
    }

    /** Held row = crossed out, no confirmation; new row = deleted after confirmation (owner, smoke 3). */
    async onProtectionRemoveClicked(row: PhaProtectionRow): Promise<void> {
        if (this.refuseIfLocked(row.id)) {
            return;
        }
        if (row.origin !== 'held') {
            try {
                await this.confirmationDialog.show([`Remove the ${row.displayName} ${row.strategyType || 'protection row'}?`]);
            } catch (e) {
                return;
            }
        }
        this.sheet.removeStrategy('protection', row.id);
        this.publishSheet();
    }

    onProtectionRestoreClicked(row: PhaProtectionRow): void {
        this.sheet.restoreStrategy('protection', row.id);
        this.publishSheet();
    }

    /** The row's SIDE picked (item 4): the sign of its dollars follows. */
    onProtectionSideChanged(ev: {row: PhaProtectionRow, side: PhaSide}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this.sheet.setProtectionSide(ev.row.id, ev.side);
        this.publishSheet();
    }

    /** The option ticker for a protection row's (expiration, strike) — by the row's own type (a PUT unless loaded otherwise). */
    private buildProtectionTicker(p: PhaProtectionRow): void {
        if (!p) {
            return;
        }
        if (!p.expiration || p.strike === null) {
            this.sheet.setProtectionOptionTicker(p.id, null);
            return;
        }
        const descriptor = p.expiration.descriptor as OptionExpirationDescriptor;
        this.sheet.setProtectionOptionTicker(p.id, makeOptionTicker(descriptor, optionTypeOf(p.optionType), p.strike) || null);
    }

    // ---- Premium (M3) ------------------------------------------------------------------------
    private readonly _chainByTicker: {[ticker: string]: PhaExpiration[]} = {};

    get hasStockUnderlyings(): boolean {
        return this.sheet.rows.some(u => u.assetType !== 'Call');
    }

    get hasUnderlyings(): boolean {
        return this.sheet.rows.length > 0;
    }

    // ---- the OTM movers (R15, owner 2026-09-29 vrequest #2) ---------------------------------------
    //
    // One per section, APG "Strike Step" look: a step box between a down and an up arrow. A click
    // SHIFTS every live row's own % OTM by the step (a move, never a target — the old absolute
    // "% OTM" setter is gone). Clicks in quick succession are COALESCED: the owner asked for "a
    // little delay before accepting the value", so the arrows accumulate a pending delta (shown
    // beside them) and the sheet is stepped once when the clicks stop. A session-locked row is skipped.

    /** The steps on offer (owner, 2026-09-29: 1 / 5 / 10, then 15 / 20 / 25 added the same day — item 3; default 1). */
    readonly otmSteps = PHA_OTM_STEPS;
    targetDte = {premium: 14, protection: 14};
    /** The step of each mover. */
    otmStep: {premium: number, protection: number} = {premium: 1, protection: 1};

    stepLabel(step: number): string {
        return step === null || step === undefined ? '' : step + '%';
    }
    /** What the arrows have accumulated and not yet applied (shown as "+3%" beside the mover). */
    otmPending: {premium: number, protection: number} = {premium: 0, protection: 0};
    private _otmTimers: {premium: any, protection: any} = {premium: null, protection: null};
    private static readonly OTM_MOVER_SETTLE_MS = 400;

    @DetectMethodChanges()
    onOtmStepChanged(section: 'premium' | 'protection', ev: {value: number, event?: any}): void {
        if (isVoid(ev) || isVoid(ev.event) || ev.value === null || ev.value === undefined || isNaN(ev.value)) {
            return;
        }
        if (this.optimizing || !this.isUnlocked) { return; }
        this.otmStep[section] = this.otmSteps.indexOf(ev.value) >= 0 ? ev.value : 1;
        this.publishSheet();
    }

    @DetectMethodChanges()
    onTargetDteChanged(section: 'premium' | 'protection', ev: {value: number, event?: any, component?: any}): void {
        if (this.optimizing) { return; }
        // NumberBox may commit a typed value without attaching a native event (unlike SelectBox).
        if (!this.isUnlocked || !ev) { return; }
        if (!Number.isInteger(ev.value) || ev.value < 0) {
            if (ev.component) { ev.component.option('value', this.targetDte[section]); }
            return;
        }
        if (ev.value === this.targetDte[section]) { return; }
        this.targetDte[section] = ev.value;
        this.publishSheet();
    }

    @DetectMethodChanges()
    onOtmMoverClicked(section: 'premium' | 'protection', sign: 1 | -1): void {
        if (this.optimizing) { return; }
        const rows = section === 'premium' ? this.sheet.premiumRows : this.sheet.protectionRows;
        if (!this.isUnlocked || !rows.length) {
            return;
        }
        this.otmPending[section] += sign * this.otmStep[section];
        if (this._otmTimers[section]) {
            clearTimeout(this._otmTimers[section]);
        }
        this._otmTimers[section] = setTimeout(() => this.applyOtmShift(section), PortfolioHedgingAnalysisPanelComponent.OTM_MOVER_SETTLE_MS);
    }

    private applyOtmShift(section: 'premium' | 'protection'): void {
        this._otmTimers[section] = null;
        const delta = this.otmPending[section];
        this.otmPending[section] = 0;
        if (!delta) {
            this._changeDetector.detectChanges();
            return;
        }
        if (section === 'premium') {
            const changed = this.sheet.shiftPremiumPctOtm(delta);
            changed.forEach(p => this.buildOptionTicker(p));
            console.info('[pha] premium %OTM shift ' + (delta > 0 ? '+' : '') + delta + '; re-selected ' + changed.length + ' strike(s)');
        } else {
            const changed = this.sheet.shiftProtectionPctOtm(delta);
            changed.forEach(p => this.buildProtectionTicker(p));
            console.info('[pha] protection %OTM shift ' + (delta > 0 ? '+' : '') + delta + '; re-selected ' + changed.length + ' strike(s)');
        }
        this.publishSheet();
    }

    private cancelOtmMovers(): void {
        (['premium', 'protection'] as Array<'premium' | 'protection'>).forEach(s => {
            if (this._otmTimers[s]) {
                clearTimeout(this._otmTimers[s]);
                this._otmTimers[s] = null;
            }
            this.otmPending[s] = 0;
        });
    }

    onPremiumPctOtmPicked(ev: {row: PhaPremiumRow, pct: number}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        const p = this.sheet.setPremiumPctOtm(ev.row.id, ev.pct);
        if (!p) {
            return;
        }
        this.buildOptionTicker(p);
        this.publishSheet();
    }

    onProtectionPctOtmPicked(ev: {row: PhaProtectionRow, pct: number}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        const p = this.sheet.setProtectionPctOtm(ev.row.id, ev.pct);
        if (!p) {
            return;
        }
        this.buildProtectionTicker(p);
        this.publishSheet();
    }

    /** The row's OTM lock (R18): flipped on the sheet; the mover skips a locked row. */
    onPremiumOtmLockToggled(row: PhaPremiumRow): void {
        this.sheet.togglePremiumOtmLock(row.id);
        this.publishSheet();
    }

    onProtectionOtmLockToggled(row: PhaProtectionRow): void {
        this.sheet.toggleProtectionOtmLock(row.id);
        this.publishSheet();
    }

    @DetectMethodChanges()
    onPopulateClicked(): void {
        if (this.optimizing || !this.isUnlocked) {
            return;
        }
        const added = this.sheet.populatePremium();
        added.forEach(p => this.initializeOption(p, 'premium'));
        if (!added.length) {
            this._toastr.info('Every Underlying Already Has a Premium Row');
        }
        this.publishSheet();
    }

    @DetectMethodChanges()
    onNewPremiumClicked(): void {
        if (this.optimizing || !this.isUnlocked) {
            return;
        }
        if (this.selectedOptionAddType.premium === 'Two-for-One') {
            const row = this.sheet.addTwoForOne();
            if (row) { this.initializeOption(row, 'premium'); }
            this.publishSheet();
            return;
        }
        // Prefer the first stock; without one, start on the first available index choice.
        const u = this.sheet.countingRows.find(r => r.assetType !== 'Call')
            || this.sheet.rows.find(r => r.assetType !== 'Call');
        const choice = u ? this.sheet.premiumChoices().find(c => c.underlyingRowId === u.id)
            : this.sheet.premiumChoices().find(c => c.underlyingRowId === null);
        if (!choice) { return; }
        const p = choice.underlyingRowId !== null ? this.sheet.addPremiumRow(choice.underlyingRowId)
            : this.sheet.addIndexPremiumRow(choice.ticker);
        const type = this.selectedOptionAddType && this.selectedOptionAddType.premium || 'Single Option';
        if (type !== 'Single Option') { this.sheet.linkOptionStrategy('premium', p.id, type, this.defaultStrategyGaps(p.ticker)); }
        this.initializeOption(p, 'premium');
        this.publishSheet();
    }

    onPremiumUnderlyingPicked(ev: {row: PhaPremiumRow, key: string}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        const choice = this.sheet.premiumChoices().find(c => c.key === ev.key);
        const group = this.sheet.strategyRows('premium', ev.row.id);
        if (!choice || group.some(r => r.origin === 'held' || r.crossedOut || this.sheet.isRowDisabled(r.id))) { return; }
        const changed = this.sheet.repointOptionStrategy('premium', ev.row.id, ev.key);
        if (changed.length) { this.initializeOption(changed[0], 'premium'); }
        this.publishSheet();
    }

    onPremiumExpirationPicked(ev: {row: PhaPremiumRow, expiration: PhaExpiration | null}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this._optionRequests[ev.row.id] = ++this._optionRequest;
        const p = this.sheet.setPremiumExpiration(ev.row.id, ev.expiration);
        this.buildOptionTicker(p);
        this.publishSheet();
    }

    onPremiumStrikePicked(ev: {row: PhaPremiumRow, strike: number | null}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this._optionRequests[ev.row.id] = ++this._optionRequest;
        const p = this.sheet.setPremiumStrike(ev.row.id, ev.strike);
        this.buildOptionTicker(p);
        this.publishSheet();
    }

    onPremiumContractsPicked(ev: {row: PhaPremiumRow, contracts: number}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this.sheet.setPremiumContracts(ev.row.id, ev.contracts);
        const row = this.sheet.premiumRows.find(p => p.id === ev.row.id);
        if (row && row.contractsSelling < ev.contracts) {
            // The selector never offers more than available; this is the guardrail's own record (section 8).
            console.warn('[pha] contracts over available refused: ' + row.ticker + ' asked ' + ev.contracts + ', kept ' + row.contractsSelling);
        }
        this.publishSheet();
    }

    /** Held row = crossed out, no confirmation; new row = deleted after confirmation (owner, smoke 3). */
    async onPremiumRemoveClicked(row: PhaPremiumRow): Promise<void> {
        if (this.refuseIfLocked(row.id)) {
            return;
        }
        if (row.origin !== 'held') {
            try {
                await this.confirmationDialog.show([`Remove the ${row.displayName} ${row.strategyType || 'premium row'}?`]);
            } catch (e) {
                return;
            }
        }
        this.sheet.removeStrategy('premium', row.id);
        this.publishSheet();
    }

    onPremiumRestoreClicked(row: PhaPremiumRow): void {
        this.sheet.restoreStrategy('premium', row.id);
        this.publishSheet();
    }

    /** The row's SIDE picked (item 4): the sign of its dollars follows. */
    onPremiumSideChanged(ev: {row: PhaPremiumRow, side: PhaSide}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this.sheet.setPremiumSide(ev.row.id, ev.side);
        this.publishSheet();
    }

    /** The option ticker for a row's (expiration, strike) — by the row's own type (a CALL unless loaded otherwise). */
    private buildOptionTicker(p: PhaPremiumRow): void {
        if (!p) {
            return;
        }
        if (!p.expiration || p.strike === null) {
            this.sheet.setPremiumOptionTicker(p.id, null);
            return;
        }
        const descriptor = p.expiration.descriptor as OptionExpirationDescriptor;
        this.sheet.setPremiumOptionTicker(p.id, makeOptionTicker(descriptor, optionTypeOf(p.optionType), p.strike) || null);
    }

    private _optionRequest = 0;
    private readonly _optionRequests: {[id: number]: number} = {};

    /** Only explicit new-row entry paths default an expiration; import/restore never call this. */
    @DetectMethodChanges({isAsync: true})
    private async initializeOption(row: PhaPremiumRow | PhaProtectionRow, section: 'premium' | 'protection'): Promise<void> {
        const request = ++this._optionRequest;
        this._optionRequests[row.id] = request;
        const sourceVersion = this._sourceVersion;
        const symbol = section === 'premium' ? (row as PhaPremiumRow).ticker : (row as PhaProtectionRow).symbol;
        const targetDte = this.targetDte[section];
        // Capture the initial offset before loading, just like DTE; later selector changes affect future additions.
        const creationPctOtm = section === 'premium' ? this.otmStep.premium : 0;
        const group = this.sheet.strategyRows(section, row.id);
        group.forEach(r => this._optionRequests[r.id] = request);
        const signature = () => JSON.stringify([row.expiration, row.strike, row.pctOtm, row.side,
            section === 'premium' ? (row as PhaPremiumRow).contractsSelling : (row as PhaProtectionRow).contracts]);
        const groupSignature = () => JSON.stringify(group.map(r => [r.id, r.expiration, r.strike, r.expectedStrike,
            r.strategyGap, r.side, 'contractsSelling' in r ? r.contractsSelling : r.contracts]));
        const initial = signature(), initialGroup = groupSignature();
        const twoForOne = row.strategyType === 'Two-for-One' && row.strategyLeg === 0;
        if (twoForOne) {
            this._twoForOnePending[row.id] = {signature: this.twoForOneSignature(row as PhaPremiumRow), next: Date.now() + 30000, loading: true};
        }
        const expirations = await this.loadChain(symbol);
        const rows = section === 'premium' ? this.sheet.premiumRows : this.sheet.protectionRows;
        const currentSymbol = section === 'premium' ? (row as PhaPremiumRow).ticker : (row as PhaProtectionRow).symbol;
        if (sourceVersion !== this._sourceVersion || rows.indexOf(row as any) < 0
            || group.some(r => rows.indexOf(r as any) < 0 || this._optionRequests[r.id] !== request)
            || currentSymbol !== symbol) { return; }
        const editedWhileLoading = signature() !== initial || groupSignature() !== initialGroup;
        if (section === 'premium') { this.sheet.setPremiumExpirations(symbol, expirations); }
        else { this.sheet.setProtectionExpirations(symbol, expirations); }
        if (row.origin === 'new' && (!row.expiration || twoForOne && row.strike === null && row.expectedStrike === undefined)
            && !row.crossedOut && !this.sheet.sessionLockedRowIds[row.id]
            && signature() === initial && groupSignature() === initialGroup) {
            const expiration = row.expiration || nearestExpiration(expirations.filter(e => !this._chains.isExpirationExpired(e.date)),
                targetDte, date => this._chains.getDaysToExpiration(date));
            if (expiration) {
                if (section === 'premium') {
                    this.sheet.initializeOptionExpiration(section, row.id, expiration, creationPctOtm);
                    this.buildOptionTicker(row as PhaPremiumRow);
                } else {
                    this.sheet.initializeOptionExpiration(section, row.id, expiration);
                    this.buildProtectionTicker(row as PhaProtectionRow);
                }
            }
        }
        if (twoForOne) {
            if (editedWhileLoading || row.strike !== null || this.sheet.contractConflict) { delete this._twoForOnePending[row.id]; }
            else {
                this._twoForOnePending[row.id] = {signature: this.twoForOneSignature(row as PhaPremiumRow), next: Date.now() + 30000, loading: false};
            }
        }
        this.publishSheet();
    }

    /** One chain per ticker per panel life (Premium and Protection share it). */
    private async loadChain(ticker: string, required = false): Promise<PhaExpiration[]> {
        if (this._chainByTicker[ticker]) {
            return this._chainByTicker[ticker];
        }
        let expirations: PhaExpiration[] = [];
        try {
            const chain = await this._chains.getChain(ticker);
            expirations = (chain && chain.expirations || [])
                .filter(e => e.strikes && e.strikes.length && !this._chains.isExpirationExpired(e.optionExpirationDate))
                .map(e => ({
                    date: e.optionExpirationDate,
                    label: e.dateWithDaysToExpiration || e.optionExpirationDisplayDate || e.optionExpirationDate,
                    strikes: e.strikes.slice().sort((a, b) => a - b),
                    descriptor: e
                }));
        } catch (error) {
            console.error('[pha] option chain FAILED for ' + ticker, error);
            if (required) { throw error; }
            this._toastr.warning(`The Option Chain for ${ticker} Could Not Be Loaded`);
            return []; // A failed request must remain retryable, not become a cached empty chain.
        }
        if (!expirations.length) {
            console.warn('[pha] no listed expirations for ' + ticker);
        }
        this._chainByTicker[ticker] = expirations;
        return expirations;
    }

    /** Last price when the feed has one, else the mid. A quote with neither leaves the row as is. */
    private applyQuote(q: QuoteDto): boolean {
        if (parseOptionTicker(q.ticker) && this._chains.isOptionExpired(q.ticker)) { return false; }
        const call = this.sheet.rows.some(r => r.ticker === q.ticker && r.assetType === 'Call');
        const px = call ? optionPrice(finiteOrNull(q.bid), finiteOrNull(q.ask), finiteOrNull(q.mid), finiteOrNull(q.lastPx)) : pickPrice(q);
        if (px === null) {
            return false;
        }
        this._lastQuoteAt[q.ticker] = quoteInstant(q);
        return this.sheet.setPrice(q.ticker, px, false);
    }

    // ---- stale prices (system-description.md section 5 "Failure paths") ----------------------
    // A row whose quote has not moved for STALE_AFTER_MS keeps its last price, faded, with a tooltip.
    // Nothing is cleared and balancing still works on the last price. Re-checked on a timer; the
    // feed's own timestamp is used when it carries one.
    private readonly _lastQuoteAt: {[ticker: string]: number} = {};
    private _staleTimer: any = null;

    private checkStalePrices(): void {
        const now = Date.now();
        this.sheet.recompute();
        this.reconcileSettlementPrices();
        let changed = this.sheet.rows.some(r => this.sheet.isExpired(r))
            || this.sheet.premiumRows.some(r => this.sheet.isExpired(r)) || this.sheet.protectionRows.some(r => this.sheet.isExpired(r));
        this.sheet.rows.forEach(r => {
            if (this.sheet.isExpired(r) || r.price === null) {
                return;
            }
            const at = this._lastQuoteAt[r.ticker];
            const stale = at === undefined ? false : (now - at) > STALE_AFTER_MS;
            if (stale !== r.priceStale) {
                r.priceStale = stale;
                changed = true;
                if (stale) {
                    console.warn('[pha] price stale for ' + r.ticker + ': last quote ' + Math.round((now - at) / 60000) + ' min ago');
                }
            }
        });
        if (changed) {
            this.publishSheet();
        }
    }

    // ---- the section-8 measures, as log lines (one per state change) --------------------------
    private _lastRedTotal = false;
    private _lastUnpriced = 0;
    private _lastCountsText = '';

    private logStateChanges(): void {
        const red = this.sheet.rows.length > 0 && (!this.sheet.totals.pctConsistent || !this.sheet.totals.dollarsConsistent);
        if (red !== this._lastRedTotal) {
            this._lastRedTotal = red;
            console.info('[pha] red total ' + (red ? 'ON (' + this.sheet.totals.pct + '%, $' + this.sheet.totals.dollars
                + ' of $' + this.sheet.accountValue + ', limit $' + this.sheet.maxStockValue + ')' : 'OFF'));
        }
        const unpriced = this.sheet.premiumTotals.unpricedRows + this.sheet.protectionTotals.unpricedRows;
        if (unpriced !== this._lastUnpriced) {
            this._lastUnpriced = unpriced;
            console.info('[pha] unpriced option rows: ' + unpriced);
        }
        // Pending blocks by status (§8 "Measure"): logged whenever the tally changes.
        const c = this.changeSet.counts.total;
        const countsText = c.notExported + '/' + c.notAcquired + '/' + c.overdue;
        if (countsText !== this._lastCountsText) {
            this._lastCountsText = countsText;
            console.info('[pha] pending blocks: ' + c.notExported + ' not exported, ' + c.notAcquired + ' not acquired, ' + c.overdue + ' overdue'
                + (this._analysis.sessionId ? ' (session ' + this._analysis.sessionId + ')' : ''));
        }
    }

    // ---- table events -----------------------------------------------------------------------
    onSharesTyped(ev: {row: PhaUnderlyingRow, shares: number}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this.sheet.setShares(ev.row.id, ev.shares);
        this.publishSheet();
    }

    onPercentTyped(ev: {row: PhaUnderlyingRow, pct: number}): void {
        if (this.refuseIfLocked(ev.row.id)) {
            return;
        }
        this.sheet.setPercentRequest(ev.row.id, ev.pct);
        this.publishSheet();
    }

    onSharesLockToggled(row: PhaUnderlyingRow): void {
        if (this.optimizing) { return; }
        this.sheet.toggleSharesLock(row.id);
        this.publishSheet();
    }

    onPctLockToggled(row: PhaUnderlyingRow): void {
        if (this.optimizing) { return; }
        this.sheet.togglePctLock(row.id);
        this.publishSheet();
    }

    async onRemoveClicked(row: PhaUnderlyingRow): Promise<void> {
        if (this.refuseIfLocked(row.id)) {
            return;
        }
        // Its option rows follow it (crossed out / deleted with it): a copied one must be revoked first.
        const lockedOption = this.sheet.premiumRows.find(p => p.underlyingRowId === row.id && this.sheet.isSessionLocked(p.id))
            || this.sheet.protectionRows.find(p => p.symbol === row.ticker && this.sheet.isSessionLocked(p.id));
        if (lockedOption) {
            this._toastr.warning(`${row.displayName} has an option order exported to ToS; revoke it (or apply its fill in Portfolio Hedging) before removing the underlying`);
            return;
        }
        // A held row is CROSSED OUT (reversible: no confirmation — owner, 2026-09-29 smoke 3); a new
        // row is deleted (destructive: confirm first, section 6).
        if (row.origin !== 'held') {
            try {
                await this.confirmationDialog.show([`Remove ${row.displayName} from the analysis?`]);
            } catch (e) {
                return;
            }
        }
        const what = this.sheet.removeUnderlying(row.id);
        if (what === 'deleted') {
            this.unsubscribePrice(row.ticker);
        }
        this.publishSheet();
    }

    onRestoreClicked(row: PhaUnderlyingRow): void {
        this.sheet.restoreUnderlying(row.id);
        this.publishSheet();
    }

    /** The row's SIDE picked (item 4): a label only on an underlying in v1. */
    onUnderlyingSideChanged(ev: {row: PhaUnderlyingRow, side: PhaSide}): void {
        this.sheet.setUnderlyingSide(ev.row.id, ev.side);
        this.publishSheet();
    }

    /** The editors of a locked row are disabled; this is the belt to those braces (§6.7 "no edits until revoked"). */
    private refuseIfLocked(rowId: number): boolean {
        if (this.optimizing) { return true; }
        if (this.isExpiredRow(rowId)) { return true; }
        if (!this.isUnlocked) {
            this._toastr.info(this.accountGateHint);
            this._changeDetector.detectChanges();
            return true;
        }
        if (!this.sheet.isSessionLocked(rowId)) {
            return false;
        }
        this._toastr.warning('This row\'s order is exported to ToS: revoke the export (or apply its fill in Portfolio Hedging) before editing it');
        this._changeDetector.detectChanges();
        return true;
    }

    // ---- EXPORT: copy an order block to ToS (§6.4) -----------------------------------------------
    // One block per copy (the clipboard holds one). Row Copy = the row's block; section Copy = the
    // section's one pending block, or the export window when there are several; Copy All = the
    // export window over the panel. Before any copy: the design goes to the Shell (so the block
    // exists there), a homeless session gets a home, and the ORPHAN GATE is asked with a query
    // (a refused command carries no text — M1). The block becomes Exported: green earmark, rows locked.

    /** The block a row belongs to, from the current change set; null when the row has no change. */
    private blockOfRow(rowId: number): PhaBlock | null {
        const m = this.changeSet.marks[rowId];
        return m && m.blockId ? (this.changeSet.blocks.find(b => b.blockId === m.blockId) || null) : null;
    }

    onUnderlyingCopyClicked(row: PhaUnderlyingRow): void {
        this.copyRowBlock(row.id);
    }

    onPremiumCopyClicked(row: PhaPremiumRow): void {
        this.copyRowBlock(row.id);
    }

    onProtectionCopyClicked(row: PhaProtectionRow): void {
        this.copyRowBlock(row.id);
    }

    /**
     * The row's Export icon (owner 2026-09-30: every row "Copy" icon is an EXPORT item): opens the Export
     * window on that row's order only — the copy itself happens in the window, as everywhere else.
     */
    private copyRowBlock(rowId: number): void {
        if (this.refuseDuplicateContracts()) { return; }
        if (this.isExpiredRow(rowId)) { return; }
        const block = this.blockOfRow(rowId);
        if (!block) {
            this._toastr.info('This row has no change to export');
            return;
        }
        this.openExportDialog(null, null, rowId);
    }

    /** The header's Export...: the export window over the whole panel (copy per block inside; owner vrequest 2026-09-30). */
    @DetectMethodChanges()
    onExportClicked(): void {
        if (this.optimizing || !this.isUnlocked) {
            return;
        }
        if (this.refuseDuplicateContracts()) { return; }
        this.openExportDialog(null);
    }

    private refuseDuplicateContracts(): boolean {
        const conflicts = duplicateContracts(this.sheet);
        if (!conflicts.length) { return false; }
        this._toastr.warning('Duplicate Contract: ' + conflicts[0].description + '. Choose Distinct Contracts Before Exporting.');
        return true;
    }

    /** One block (the row icon, the window's block icon). */
    private async copyBlock(block: PhaBlock): Promise<void> {
        await this.copyBlocks([block]);
    }

    /**
     * Copy to ToS: ONE block (= one ToS order, the minimum executable thing), or an underlying's option
     * CHAIN (its pending main + linked blocks, the linked lines with " TRG BY") in one clipboard write —
     * or several independent chains (owner 2026-10-02). Every copied block becomes Exported.
     */
    exportCopyBusy = false;

    private async copyBlocks(blocks: PhaBlock[]): Promise<void> {
        if (this.exportCopyBusy) { return; }
        this.exportCopyBusy = true;
        this._changeDetector.detectChanges();
        try {
            await this.copyPendingBlocks(blocks);
        } finally {
            this.exportCopyBusy = false;
            this._changeDetector.detectChanges();
        }
    }

    private async copyPendingBlocks(blocks: PhaBlock[]): Promise<void> {
        if (!this.sourceReady) { return; }
        if (this.refuseDuplicateContracts()) { return; }
        const pending = blocks.filter(b => b.status === 'Pending');
        if (pending.some(b => b.changes.some(c => c.kind === 1 && this._chains.isOptionExpired(c.tradingInstrumentCode)))) {
            this._toastr.warning('Expired Options Cannot Be Exported');
            return;
        }
        if (!pending.length) {
            this._toastr.info('This order is already exported; revoke it to export it again');
            return;
        }
        const containsUnderlyingBuy = pending.some(b => b.changes.some(c => c.section === 'Underlyings' && c.side === 1));
        if (containsUnderlyingBuy) {
            if (this.sheet.countingRows.some(r => r.price === null || !(r.price > 0))) {
                this._toastr.warning('An underlying price is missing. Refresh quotes before exporting underlying orders.');
                return;
            }
            if (!this.sheet.totals.dollarsConsistent) {
                this._toastr.warning('Underlying value exceeds the maximum allocation. Reduce exposure before exporting underlying buys.');
                return;
            }
        }
        if (!this._analysis.isLive) {
            this._toastr.warning('There is no analysis session to export from: pick a combo or add an underlying first');
            return;
        }
        if (this._analysis.isHomeless) {
            // Give it a home first (§6.1): the export waits for the dialog; the trader re-clicks Copy after.
            this.openHomeDialog();
            return;
        }
        // Group bulk clipboard output by section/underlying; independent orders never use TRG BY.
        const grouped = new Map<string, PhaBlock[]>();
        pending.forEach(b => {
            const key = b.section + '|' + b.underlying + '|' + (b.changes.every(c => c.kind === 0) ? 'S' : 'O');
            if (!grouped.has(key)) { grouped.set(key, []); }
            grouped.get(key).push(b);
        });
        const chains = Array.from(grouped.values()).map(composeChainText);
        const chain = {
            perBlock: chains.reduce((all, c) => all.concat(c.perBlock), [] as {blockId: string, orderText: string}[]),
            text: chains.some(c => !c.text) ? '' : chains.map(c => c.text).join('\n')
        };
        if (!chain.text) {
            this._toastr.warning('This order cannot be written yet: a leg is missing its expiration or strike');
            return;
        }
        // The Shell must know these blocks before it can mark them Exported.
        const sent = await this._analysis.flushReplace();
        if (!sent) {
            this._toastr.error('The design could not be saved to the analysis session; nothing was exported');
            return;
        }
        // The orphan gate (owner 2026-09-30), asked before the clipboard is touched.
        const orphans = await this._analysis.countOrphansOnCombo();
        if (orphans > 0) {
            this._toastr.warning(`Clear the ${orphans} orphaned order(s) of the previous analysis on this combo in Portfolio Hedging ("Clear After State") before exporting new ones`);
            return;
        }
        // The clipboard.
        try {
            const allowed = await checkClipboardPermissions();
            if (!allowed) {
                throw new Error('clipboard-write not allowed');
            }
            await navigator.clipboard.writeText(chain.text);
        } catch (error) {
            console.error('[pha] clipboard write FAILED', error);
            this._toastr.error('Could not export: the clipboard refused the order. Please check the browser permissions');
            return;
        }
        // The record: Pending → Exported (the Shell freezes each block's line as placed, pushes the change).
        const ok = await this._analysis.export(chain.perBlock);
        if (!ok) {
            this._toastr.error('The order text is on the clipboard, but the analysis session refused to mark it exported (see the Shell log). Not placed? Paste nothing and try again');
            return;
        }
        chain.perBlock.forEach(p => {
            const b = pending.find(x => x.blockId === p.blockId);
            console.info('[pha] block ' + p.blockId + ' (' + b.section + ', ' + b.underlying + ' ' + (chainName(b) || 'stock') + ', ' + b.changes.length + ' leg(s)) EXPORTED: ' + p.orderText);
        });
        const first = pending[0];
        this._toastr.success(pending.length === 1
            ? `Exported (on the clipboard, paste into ToS): ${first.changes.length === 1 && first.changes[0].kind === 0 ? describeChange(first.changes[0]) : first.underlying + ' ' + (chainName(first) || 'order') + ' (' + first.changes.length + ' leg(s))'}`
            : `Exported (on the clipboard, paste into ToS): ${pending.length} orders`);
        this.refreshChangeSet();
        this._changeDetector.detectChanges();
    }

    // ---- REVOKE (owner 2026-09-30): block / section / session ---------------------------------------
    onUnderlyingRevokeClicked(row: PhaUnderlyingRow): void {
        this.revokeRowBlock(row.id);
    }

    onPremiumRevokeClicked(row: PhaPremiumRow): void {
        this.revokeRowBlock(row.id);
    }

    onProtectionRevokeClicked(row: PhaProtectionRow): void {
        this.revokeRowBlock(row.id);
    }

    private async revokeRowBlock(rowId: number): Promise<void> {
        if (this.isExpiredRow(rowId)) { return; }
        const block = this.blockOfRow(rowId);
        if (!block || (block.status !== 'Exported' && block.status !== 'Overdue')) {
            return;
        }
        await this.revoke('block', block.blockId, '', 'The export was revoked: the order is back to not exported and its rows are unlocked');
    }

    async onSectionRevokeClicked(section: PhaSection): Promise<void> {
        const key = sectionKeyOf(section);
        const n = this.changeSet.counts[key].notAcquired;
        if (!n) {
            return;
        }
        try {
            await this.confirmationDialog.show([`Revoke the ${n} exported order(s) of ${section.toUpperCase()}?`, 'They go back to not exported; nothing is placed or cancelled in ToS by this.']);
        } catch (e) {
            return;
        }
        await this.revoke('section', '', key, `${n} exported order(s) of ${section.toUpperCase()} revoked`);
    }

    async onRevokeAllClicked(): Promise<void> {
        const n = this.changeSet.counts.total.notAcquired;
        if (!n) {
            return;
        }
        try {
            await this.confirmationDialog.show([`Revoke every exported order (${n})?`, 'They go back to not exported; nothing is placed or cancelled in ToS by this.']);
        } catch (e) {
            return;
        }
        await this.revoke('session', '', '', `${n} exported order(s) revoked`);
    }

    private async revoke(scope: 'block' | 'section' | 'session', blockId: string, section: string, done: string): Promise<void> {
        const ok = await this._analysis.revoke(scope, blockId, section);
        if (ok) {
            this._toastr.success(done);
        } else {
            this._toastr.error('The revoke was refused by the analysis session (see the Shell log)');
        }
        this.refreshChangeSet();
        this._changeDetector.detectChanges();
    }

    // ---- the export window --------------------------------------------------------------------
    exportDialogVisible = false;
    exportDialogTitle = 'Export Orders';
    exportGroups: PhaExportGroup[] = [];

    /** Optional order scope; a split row can instead show all its contributing orders. */
    private _exportBlockId: string | null = null;
    private _exportRowId: number | null = null;

    private openExportDialog(scope: AnalysisSection | null, blockId: string | null = null, rowId: number | null = null): void {
        this._exportRowId = rowId;
        this._exportBlockId = blockId;
        const block = blockId ? this.changeSet.blocks.find(b => b.blockId === blockId) : null;
        this.exportDialogTitle = block ? 'Export Order — ' + this._displayNames.getDisplayNameForTicker(block.underlying)
            : (scope ? 'Export Orders — ' + scope.toUpperCase() : 'Export Orders');
        this.exportDialogVisible = true;
        this.refreshExportItems();
        this._changeDetector.detectChanges();
    }

    @DetectMethodChanges()
    onExportDialogClosed(): void {
        this.exportDialogVisible = false;
        this._exportBlockId = null;
        this._exportRowId = null;
        this.exportGroups = [];
    }

    async onExportBlockCopy(item: PhaExportBlock): Promise<void> {
        const block = this.changeSet.blocks.find(b => b.blockId === item.blockId);
        if (block) {
            await this.copyBlock(block);
        }
    }

    /** The underlying bar's chain copy: its pending option blocks, main first, in one write. */
    async onExportChainCopy(group: PhaExportGroup): Promise<void> {
        const ids = group.blocks.map(b => b.blockId);
        const blocks = this.changeSet.blocks.filter(b => ids.indexOf(b.blockId) >= 0 && b.status === 'Pending');
        if (blocks.length) {
            await this.copyBlocks(blocks);
        }
    }

    async onExportManyCopy(items: PhaExportBlock[]): Promise<void> {
        const ids = items.map(b => b.blockId);
        await this.copyBlocks(this.changeSet.blocks.filter(b => ids.indexOf(b.blockId) >= 0));
    }

    async onExportBlockRevoke(item: PhaExportBlock): Promise<void> {
        await this.revoke('block', item.blockId, '', 'The export was revoked: the order is back to not exported and its rows are unlocked');
    }

    /** The window's groups (section → underlying → blocks) from the current change set; only rebuilt while it is open. */
    private refreshExportItems(): void {
        if (!this.exportDialogVisible) {
            return;
        }
        const shown = this.changeSet.blocks
            .filter(b => b.changes.length > 0 && b.status !== 'Acquired' && b.status !== 'Removed' && b.status !== 'Orphaned')
            .filter(b => !this._exportBlockId || b.blockId === this._exportBlockId)
            .filter(b => this._exportRowId === null || b.changes.some(c => c.rowIds.indexOf(this._exportRowId) >= 0));
        const groups: PhaExportGroup[] = [];
        shown.forEach(b => {
            const isAsset = b.changes.every(c => c.kind === 0);
            const key = b.section + '|' + b.underlying + '|' + (isAsset ? 'S' : 'O');
            let g = groups.find(x => x.key === key);
            if (!g) {
                const name = this._displayNames.getDisplayNameForTicker(b.underlying);
                g = {
                    key,
                    section: b.section,
                    title: name || b.underlying,
                    isAsset,
                    blocks: []
                };
                groups.push(g);
            }
            g.blocks.push(this.toExportBlock(b));
        });
        this.exportGroups = groups;
        if (this.exportDialog) {
            this.exportDialog.repaint();
        }
    }

    private toExportBlock(b: PhaBlock): PhaExportBlock {
        const writable = b.status !== 'Pending' || !!composeOrderText(b);
        return {
            blockId: b.blockId,
            name: chainName(b),
            status: b.status,
            legs: b.changes.map(c => ({
                kind: c.kind,
                ticker: c.kind === 0 ? c.tradingInstrumentCode : c.underlying,
                type: c.optionType,
                expiration: c.expiration,
                strike: c.strike,
                beforeQty: c.beforeQty,
                afterQty: c.afterQty,
                tradeSide: c.side,
                tradeQty: c.qty
            })),
            blockedReason: writable ? '' : 'A leg is missing its expiration or strike',
            // To the second, 12-hour with AM/PM, in the user's default time zone (owner vrequest 2026-09-30).
            exportedText: b.exportedAtUtc ? 'Exported ' + this._timestamps.getZonedFormattedDateTime(new Date(b.exportedAtUtc), 'dd-MMM-yy hh:mm:ss a') : ''
        };
    }

    // ---- "give it a home" (§6.1): a homeless session before its first export -----------------------
    homeDialogVisible = false;
    homeNewPortfolioName = '';
    homeComboName = '';
    homeStartingCapital: number | null = null;
    accountCashDialog = {visible: false, kind: 1, amount: null as number};
    homeBusy = false;

    @DetectMethodChanges()
    private openHomeDialog(): void {
        this.homeNewPortfolioName = '';
        this.homeComboName = '';
        this.homeStartingCapital = this.sheet.accountValue;
        this.homeBusy = false;
        this.homeDialogVisible = true;
        this._toastr.info('This analysis has no home yet: give it a portfolio and a combo, then export the order again');
    }

    @DetectMethodChanges()
    onHomeNewPortfolioNameChanged(ev: {value: string}): void {
        this.homeNewPortfolioName = isVoid(ev) ? '' : (ev.value || '');
    }

    @DetectMethodChanges()
    onHomeComboNameChanged(ev: {value: string}): void {
        this.homeComboName = isVoid(ev) ? '' : (ev.value || '');
    }

    @DetectMethodChanges()
    onHomeStartingCapitalChanged(ev: {value: number | null}): void {
        this.homeStartingCapital = isVoid(ev) ? null : ev.value;
    }

    get canAssignHome(): boolean {
        const combo = (this.homeComboName || '').trim();
        if (!combo || this.homeBusy) {
            return false;
        }
        return this.homeStartingCapital != null && Number.isFinite(this.homeStartingCapital) && this.homeStartingCapital > 0
            && !!(this.homeNewPortfolioName || '').trim();
    }

    openAccountCash(kind: number): void {
        const combo = this.allCombos.find(c => c.comboId === this.selectedComboId);
        if (!combo || !combo.hasStartingCapital) { this._toastr.warning('Select a combo with starting capital first'); return; }
        this.accountCashDialog = {visible: true, kind, amount: null};
        this._changeDetector.detectChanges();
    }

    async onRecordAccountCash(): Promise<void> {
        if (this.optimizing) { return; }
        const combo = this.allCombos.find(c => c.comboId === this.selectedComboId);
        if (!combo || !(this.accountCashDialog.amount > 0)) { this._toastr.warning('Enter a valid amount'); return; }
        try {
            await this._shellClient.processCommand(new RecordCollarsAccountCash(combo.ownerTerminalId, combo.comboId,
                this.accountCashDialog.kind, this.accountCashDialog.amount));
            this.accountCashDialog.visible = false;
            await this.loadPortfolios();
            const refreshed = this.allCombos.find(c => c.comboId === combo.comboId);
            if (refreshed && refreshed.hasStartingCapital) {
                this.sheet.setAccountValue(refreshed.accountValue);
                this.publishSheet();
            }
            this._changeDetector.detectChanges();
        } catch (error) {
            this._toastr.error(error && error.message ? error.message : 'Account cash operation failed');
        }
    }

    closeAccountCashDialog(): void { this.accountCashDialog.visible = false; this._changeDetector.detectChanges(); }

    @DetectMethodChanges()
    onHomeCancelled(): void {
        this.homeDialogVisible = false;
    }

    @DetectMethodChanges({isAsync: true})
    async onHomeConfirmed(): Promise<void> {
        if (!this.canAssignHome) {
            return;
        }
        const ownTerminalId = this._session.loginResult && this._session.loginResult.ownTerminal
            ? this._session.loginResult.ownTerminal.terminalId : null;
        const terminalId = ownTerminalId;
        if (!terminalId) {
            this._toastr.error('Your terminal could not be determined; the analysis cannot be given a home');
            return;
        }
        this.homeBusy = true;
        this._changeDetector.detectChanges();
        const comboName = this.homeComboName.trim();
        const newPortfolioName = this.homeNewPortfolioName.trim();
        const dto = await this._analysis.assignHome(terminalId, null, newPortfolioName, comboName,
            this.homeStartingCapital as number);
        if (!dto) {
            this.homeBusy = false;
            this._toastr.error('The home could not be assigned (a name may already exist, or the portfolio is archived); see the Shell log');
            this._changeDetector.detectChanges();
            return;
        }
        // The combo now exists in Collars: re-list, select it (no reload — the design stays), re-read the session by combo.
        this.homeDialogVisible = false;
        this._changeDetector.detectChanges();
        await this.loadPortfolios();
        const portfolios: PhaPortfolioChoice[] = [];
        this.portfolioGroups.forEach(g => portfolios.push(...g.items));
        const home = portfolios.find(p => p.name === newPortfolioName && p.ownerTerminalId === terminalId);
        const combo = home ? home.combos.find(c => c.name === comboName) : null;
        if (!home || !combo) {
            this._toastr.warning('The home was created but could not be found in the list; re-open the analysis from Portfolio Hedging');
            this.homeBusy = false;
            this._changeDetector.detectChanges();
            return;
        }
        this.selectedPortfolio = home;
        this.combos = home.combos.slice();
        this.selectedComboId = combo.comboId;
        this.sheet.portfolio = {
            comboId: combo.comboId, comboName: combo.name, portfolioId: home.portfolioId, portfolioName: home.name, ownerUserName: home.ownerUserName
        };
        await this._analysis.read(combo.comboId);
        this.sheet.setAccountValue(combo.accountValue);
        this._lastDesignFingerprint = '';   // the changes now carry their Collars groups; send once more
        this.homeBusy = false;
        this.publishSheet();
        console.info('[pha] home assigned: ' + home.ownerUserName + ' / ' + home.name + ' / ' + combo.name + ' (' + combo.comboId + ')');
        this._toastr.success(`The analysis now lives in ${home.name} / ${combo.name}. Export the order again`);
    }

    // ---- the three sub-sections --------------------------------------------------------------
    sectionSizes: SectionSizes = {...DEFAULT_SECTION_SIZES};

    // The blown-up section, or null when the normal split is shown (ruling R5: the blown-up
    // section keeps its header and footer; the other two hide entirely).
    blownUp: PhaSection = null;

    isSectionVisible(section: PhaSection): boolean {
        return this.blownUp === null || this.blownUp === section;
    }

    @DetectMethodChanges()
    toggleBlowUp(section: PhaSection): void {
        if (this.blownUp === section) {
            this.blownUp = null;
            this.sectionSizes = {...this._rememberedSizes};
        } else {
            if (this.blownUp === null) {
                this._rememberedSizes = {...this.sectionSizes};
            }
            this.blownUp = section;
            this.sectionSizes = {
                underlyings: section === 'underlyings' ? 100 : 0,
                premium: section === 'premium' ? 100 : 0,
                protection: section === 'protection' ? 100 : 0
            };
        }
    }

    private _rememberedSizes: SectionSizes = {...DEFAULT_SECTION_SIZES};

    // The user dragged a gutter: remember the sizes so a later blow-up/restore returns to them.
    onSplitDragEnd(ev: {sizes: Array<number | '*'>}): void {
        if (this.blownUp !== null || !ev || !ev.sizes || ev.sizes.length !== 3) {
            return;
        }
        const s = ev.sizes.map(x => typeof x === 'number' ? x : 0);
        this.sectionSizes = {underlyings: s[0], premium: s[1], protection: s[2]};
        this._rememberedSizes = {...this.sectionSizes};
    }

    // ---- zoom --------------------------------------------------------------------------------
    // Scales everything between the sticky header and the sticky footer (ruling R5). CSS `zoom`
    // on purpose, like the Hedge Matrix and EtsFitToViewport: it reflows layout, `transform:
    // scale` does not.
    zoomLevel = 1;

    get zoomPercent(): number {
        return Math.round(this.zoomLevel * 100);
    }

    @DetectMethodChanges()
    zoomOut(): void {
        this.setZoom(this.zoomLevel - ZOOM_STEP);
    }

    @DetectMethodChanges()
    zoomIn(): void {
        this.setZoom(this.zoomLevel + ZOOM_STEP);
    }

    @DetectMethodChanges()
    zoomReset(): void {
        this.setZoom(1);
    }

    private setZoom(level: number): void {
        const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, level));
        this.zoomLevel = Math.round(clamped * 10) / 10;
    }

    // ---- the portfolio + combo selectors (M5, rulings R6 + R11; reworked 2026-09-29 vrequest) --
    // The COMBO is the unit of analysis (owner, 2026-09-29: a portfolio holds several combos; the
    // analysis loads one). Two independent selectors replace the old combined "owner / portfolio /
    // combo" box and the standalone owner badge (owner, 2026-09-29): first the PORTFOLIO, grouped by
    // client exactly as APG's portfolio selector (own client's group first, then alphabetical; the
    // field reads "client | portfolio"), then the COMBO, listing only that portfolio's combos (a lone
    // combo is auto-selected). The server scopes the Collars tree; a super user sees all. Picking a
    // combo IMPORTS it into a session (or restores the live one). The selectors COMPLEMENT the deep
    // link from a Collars combo row: opened from one, both are preselected.
    portfolioGroups: PhaPortfolioGroup[] = [];
    /** Group bucket id -> its name (item 10). */
    private _groupNameById: {[bucketId: string]: string} = {};
    selectedPortfolio: PhaPortfolioChoice | null = null;
    combos: PhaComboChoice[] = [];
    selectedComboId: string | null = null;
    get selectedComboChoice(): PhaComboChoice | null { return this.allCombos.find(c => c.comboId === this.selectedComboId) || null; }
    portfoliosLoading = false;
    panelLoading = false;
    private beginPanelLoad(): void {
        this.panelLoading = true;
        this._changeDetector.detectChanges();
    }

    private endPanelLoad(): void {
        // Source generations own their state; a list refresh has an independent lifetime.
        // Neither completion may hide the other's busy state (or strand it on an older token).
        this.panelLoading = this._sourceLoadState === 'loading' || !!this.portfoliosLoading;
        this._changeDetector.detectChanges();
    }

    get portfolioContext(): PhaPortfolioContext | null {
        return this.sheet.portfolio;
    }

    /** Every combo of every group, flat (deep-link lookup). */
    private get allCombos(): PhaComboChoice[] {
        const all: PhaComboChoice[] = [];
        this.portfolioGroups.forEach(g => g.items.forEach(p => all.push(...p.combos)));
        return all;
    }

    private async loadPortfolios(): Promise<boolean> {
        if (this.portfoliosLoading) {
            return false;
        }
        this.portfoliosLoading = true;
        let succeeded = false;
        try {
            this.beginPanelLoad();
            const terminals = await this._shellClient.processQuery<CollarsTerminalDto[]>(new GetCollarsAccessibleTerminals());
            const buckets = await this._shellClient.processQuery<CollarsBucketDto[]>(new ListCollarsBuckets());
            const ownTerminalId = this._session.loginResult && this._session.loginResult.ownTerminal
                ? this._session.loginResult.ownTerminal.terminalId : null;
            const byTerminal: {[id: string]: CollarsTerminalDto} = {};
            (terminals || []).forEach(t => byTerminal[t.terminalId] = t);
            const ownerOf = (terminalId: string) => {
                const t = byTerminal[terminalId];
                return t ? (t.userName || t.displayName) : '?';
            };
            // The Group buckets (level 2) by id: a loaded combo's option positions go to the section
            // whose name their bucket carries (item 10, owner ruling 2026-09-29: by name only).
            this._groupNameById = {};
            (buckets || []).filter(b => b.level === 2).forEach(b => this._groupNameById[b.bucketId] = b.name);
            // Portfolios (level 0) with their non-archived combos (level 1).
            const portfolioById: {[id: string]: PhaPortfolioChoice} = {};
            (buckets || []).filter(b => b.level === 0 && !b.isArchived).forEach(b => {
                portfolioById[b.bucketId] = {
                    portfolioId: b.bucketId,
                    name: b.name,
                    ownerTerminalId: b.ownerTerminalId,
                    ownerUserName: ownerOf(b.ownerTerminalId),
                    combos: []
                };
            });
            (buckets || []).filter(b => b.level === 1 && !b.isArchived).forEach(b => {
                const portfolio = portfolioById[b.parentBucketId];
                if (!portfolio) {
                    return;
                }
                portfolio.combos.push({
                    comboId: b.bucketId,
                    name: b.name,
                    portfolioId: portfolio.portfolioId,
                    portfolioName: portfolio.name,
                    ownerUserName: portfolio.ownerUserName,
                    ownerTerminalId: b.ownerTerminalId,
                    hasStartingCapital: b.hasStartingCapital,
                    startingCapital: b.startingCapital,
                    freeCash: b.freeCash,
                    accountValue: b.accountValue
                });
            });
            // Group by client; the own client's group goes first (as APG), the rest alphabetical.
            const groupByOwner: {[terminalId: string]: PhaPortfolioGroup} = {};
            Object.keys(portfolioById).map(id => portfolioById[id]).forEach(p => {
                p.combos.sort((a, b) => a.name.localeCompare(b.name));
                let g = groupByOwner[p.ownerTerminalId];
                if (!g) {
                    g = {key: p.ownerUserName, isOwn: p.ownerTerminalId === ownTerminalId, items: []};
                    groupByOwner[p.ownerTerminalId] = g;
                }
                g.items.push(p);
            });
            this.portfolioGroups = Object.keys(groupByOwner).map(id => groupByOwner[id])
                .map(g => {
                    g.items.sort((a, b) => a.name.localeCompare(b.name));
                    return g;
                })
                .sort((a, b) => (a.isOwn === b.isOwn ? a.key.localeCompare(b.key) : (a.isOwn ? -1 : 1)));
            succeeded = true;
        } catch (error) {
            console.error('[pha] portfolios could not be listed', error);
            this._toastr.warning('The portfolios could not be listed');
        } finally {
            this.portfoliosLoading = false;
            try {
                if (succeeded) { this.refreshSelectedPortfolioFromGroups(); }
            } catch (error) {
                succeeded = false;
                console.error('[pha] portfolio selection refresh failed', error);
            } finally {
                this._portfolioLoadFailed = !succeeded;
                this.endPanelLoad();
            }
        }
        return succeeded;
    }

    /** Rebind selector objects to the refreshed list and apply the combo's current account value. */
    private refreshSelectedPortfolioFromGroups(): void {
        const portfolios: PhaPortfolioChoice[] = [];
        this.portfolioGroups.forEach(g => portfolios.push(...g.items));
        if (this.selectedPortfolio) {
            this.selectedPortfolio = portfolios.find(p => p.portfolioId === this.selectedPortfolio.portfolioId) || null;
            this.combos = this.selectedPortfolio ? this.selectedPortfolio.combos.slice() : [];
        }
        if (this.selectedComboId) {
            const combo = this.allCombos.find(c => c.comboId === this.selectedComboId);
            if (combo) {
                this.selectedPortfolio = portfolios.find(p => p.portfolioId === combo.portfolioId) || this.selectedPortfolio;
                this.combos = this.selectedPortfolio ? this.selectedPortfolio.combos.slice() : [];
            }
            this.sheet.setAccountValue(combo && combo.hasStartingCapital ? combo.accountValue : null);
            this.publishSheet();
        }
    }

    async onPortfolioSelected(ev: {value: PhaPortfolioChoice, event?: any}): Promise<void> {
        if (this.optimizing) { return; }
        if (isVoid(ev) || isVoid(ev.event)) {
            return;
        }
        if (!await this.setPortfolio(ev.value || null)) { return; }
        // A portfolio with exactly one combo: no second pick to make (owner ruling, 2026-09-29).
        if (this.combos.length === 1) {
            this.selectCombo(this.combos[0].comboId, true);
        }
    }

    onComboSelected(ev: {value: string, event?: any}): void {
        if (this.optimizing) { return; }
        if (isVoid(ev) || isVoid(ev.event)) {
            return;
        }
        this.selectCombo(ev.value || null, true);
    }

    /** Switch the portfolio: the combo list follows it, the previous analysis is left in its session (restorable). */
    private async setPortfolio(portfolio: PhaPortfolioChoice | null): Promise<boolean> {
        this.cancelOptimization();
        const sourceVersion = ++this._sourceVersion;
        if (!await this._analysis.flushReplace()) {
            this._toastr.warning('The Current Analysis Could Not Be Saved; Please Try Again');
            return false;
        }
        if (sourceVersion !== this._sourceVersion) { return false; }
        this.selectedPortfolio = portfolio;
        this.combos = portfolio ? portfolio.combos.slice() : [];
        this.selectedComboId = null;
        this._analysis.detach();
        this.replaceSheet(null, []);
        this.sheet.setAccountValue(null);
        this.publishSheet();
        // No selected combo means the independent no-portfolio analysis. A lone combo loads next.
        if (this.combos.length !== 1) { await this.restoreHomelessIfAny(); }
        return true;
    }

    /** Import a combo (§6.1): start its session or restore the live one; the sheet follows. `byUser` false = a deep link. */
    private async selectCombo(comboId: string | null, byUser: boolean): Promise<boolean> {
        this.cancelOptimization();
        return this.runSourceLoad(async sourceVersion => {
            if (!await this._analysis.flushReplace()) {
                this._toastr.warning('The Current Analysis Could Not Be Saved; Please Try Again');
                return false;
            }
            if (sourceVersion !== this._sourceVersion) { return false; }
            this.selectedComboId = comboId;
            this._analysis.detach();
            if (!comboId) {
                this.replaceSheet(null, []);
                this.sheet.setAccountValue(null);
                this.publishSheet();
                return this.restoreHomelessSource(sourceVersion);
            }
            const choice = this.combos.find(p => p.comboId === comboId);
            // A prior selection or saved analysis state must never carry account value into this combo.
            this.sheet.setAccountValue(choice && choice.hasStartingCapital ? choice.accountValue : null);
            this.publishSheet();
            const context: PhaPortfolioContext = {
                comboId,
                comboName: choice ? choice.name : comboId,
                portfolioId: choice ? choice.portfolioId : null,
                portfolioName: choice ? choice.portfolioName : '?',
                ownerUserName: choice ? choice.ownerUserName : '?'
            };

            const dto = await this._analysis.startOrRestore(comboId);
            if (sourceVersion !== this._sourceVersion) {
                return false; // the trader moved on while the Shell answered
            }
            if (!dto) {
                throw new Error('The analysis session could not be started on this combo');
            }
            const restored = dto && (dto.sheetStateJson || (dto.blocks && dto.blocks.length)) ? parseSheetState(dto.sheetStateJson) : null;
            if (restored) {
                // RESTORE (§6.1): the saved design comes back exactly as it was, every change and status.
                this._lastDesignFingerprint = '';
                await this.applySheetState(context, restored, comboId, sourceVersion);
                if (sourceVersion !== this._sourceVersion) {
                    return false;
                }
                this.sheet.setAccountValue(choice && choice.hasStartingCapital ? choice.accountValue : null);
                this.publishSheet();
                console.info(`[pha] session ${dto.sessionId} restored on ${context.ownerUserName} / ${context.portfolioName} / ${context.comboName}: `
                    + `${this.sheet.rows.length} underlying(s), ${this.sheet.premiumRows.length} premium, ${this.sheet.protectionRows.length} protection row(s), `
                    + `${(dto.blocks || []).length} block(s)` + (byUser ? '' : ' (deep link)'));
                this._toastr.info(`Analysis Restored: ${this.countsText(this.changeSet.counts.total) || 'no pending orders'}`);
                return true;
            }
            return this.loadComboFromCollars(comboId, context, byUser, sourceVersion);
        });
    }

    /** One source owns readiness and cleanup; stale requests can never finish a newer load. */
    private async runSourceLoad(load: (version: number) => Promise<boolean>): Promise<boolean> {
        const sourceVersion = ++this._sourceVersion;
        this._sourceLoadState = 'loading';
        this._readySessionId = undefined;
        this._restoreDepth = 1;
        this.sheet.contractValidationEnabled = false;
        let succeeded = false;
        try {
            this.beginPanelLoad();
            succeeded = await load(sourceVersion);
        } catch (error) {
            console.error('[pha] analysis load failed', error);
        } finally {
            try {
                if (sourceVersion === this._sourceVersion) {
                    this._restoreDepth = 0;
                    this.sheet.contractValidationEnabled = true;
                    this._sourceLoadState = succeeded ? 'ready' : 'failed';
                    this._readySessionId = this._analysis.sessionId;
                    if (succeeded) {
                        try { this.publishSheet(); }
                        catch (error) {
                            succeeded = false;
                            this._sourceLoadState = 'failed';
                            console.error('[pha] analysis publication failed', error);
                        }
                    }
                    if (!succeeded) { this._toastr.warning('The Analysis Could Not Be Loaded; Please Retry Load'); }
                }
            } finally {
                this.endPanelLoad();
            }
        }
        return succeeded && sourceVersion === this._sourceVersion;
    }

    /** A failed read is not an absent draft. Retry must keep asking the same source. */
    private async restoreHomelessIfAny(): Promise<boolean> {
        return this.runSourceLoad(version => this.restoreHomelessSource(version));
    }

    private async restoreHomelessSource(sourceVersion: number): Promise<boolean> {
        const dto = await this._analysis.read(null, true);
        if (sourceVersion !== this._sourceVersion || this.selectedComboId) { return false; }
        if (!dto) { return true; }
        const state = parseSheetState(dto.sheetStateJson);
        if (state) {
            this._lastDesignFingerprint = '';
            await this.applySheetState(null, state, null, sourceVersion);
            if (sourceVersion !== this._sourceVersion) { return false; }
            this.publishSheet();
            console.info('[pha] homeless session ' + dto.sessionId + ' restored: ' + this.sheet.rows.length + ' underlying(s)');
        }
        return true;
    }

    /** The FIRST import of a combo: its held names + options from the Collars book (the before state). */
    private async loadComboFromCollars(comboId: string, context: PhaPortfolioContext, byUser: boolean,
                                       sourceVersion = this._sourceVersion): Promise<boolean> {
        let positions: CollarsPositionDto[] = [];
        try {
            positions = await this._shellClient.processQuery<CollarsPositionDto[]>(new ListCollarsPositions());
        } catch (error) {
            console.error('[pha] positions could not be listed for combo ' + comboId, error);
            this._toastr.warning('The combo positions could not be read');
            return false;
        }
        if (this.selectedComboId !== comboId || sourceVersion !== this._sourceVersion) {
            return false;
        }
        const inCombo = (positions || []).filter(p => p.comboId === comboId && !p.isArchived && !p.isInterest && p.netPosition !== 0);
        // Stocks/ETFs (kind 0) from ANY bucket become Underlying rows, as before.
        const mine = inCombo.filter(p => p.kind === 0);
        const shorts = mine.filter(p => p.netPosition < 0).map(p => p.tradingInstrumentCode);
        if (shorts.length) {
            // v1 refuses a short underlying (system-description.md section 6); it is left out and named.
            this._toastr.warning(`Short positions are not supported in this version and were left out: ${shorts.join(', ')}`);
        }
        const held: PhaHeldPosition[] = mine
            .filter(p => p.netPosition > 0)
            .map(p => ({
                ticker: p.tradingInstrumentCode,
                displayName: this._displayNames.getDisplayNameForTicker(p.tradingInstrumentCode) || p.tradingInstrumentCode,
                shares: p.netPosition,
                positionId: p.positionId,
                assetType: this.underlyingType(p.tradingInstrumentCode)
            }));
        // Options (item 10, owner 2026-09-29): the section comes from the BUCKET NAME only — "Premium"
        // or "Protection" (the renamed Collars defaults). An option in any other bucket is left out and named.
        const heldCalls: Array<{position: CollarsPositionDto, option: ReturnType<typeof parseOptionTicker>}> = [];
        const options: PhaHeldOption[] = [];
        const skipped: string[] = [];
        inCombo.filter(p => p.kind !== 0).forEach(p => {
            const t = parseOptionTicker(p.tradingInstrumentCode);
            const groupName = p.groupId ? this._groupNameById[p.groupId] : undefined;
            if (groupName === 'Underlyings' && t && t.type === 'Call' && p.netPosition > 0) {
                heldCalls.push({position: p, option: t});
                return;
            }
            const section = groupName === 'Premium' ? 'premium' : (groupName === 'Protection' ? 'protection' : null);
            if (!t || !section) {
                skipped.push(p.tradingInstrumentCode + (groupName ? ` [${groupName}]` : ''));
                return;
            }
            options.push({
                optionTicker: p.tradingInstrumentCode,
                section,
                underlying: t.underlying,
                displayName: this._displayNames.getDisplayNameForTicker(t.underlying) || t.underlying,
                optionType: t.type === 'Put' ? 'Put' : 'Call',
                strike: t.strike,
                expirationDate: t.expiration,
                expirationTicker: t.expirationTicker,
                contracts: Math.abs(p.netPosition),
                side: p.netPosition < 0 ? 'Sell' : 'Buy',
                positionId: p.positionId
            });
        });
        if (skipped.length) {
            console.warn('[pha] combo ' + comboId + ': option position(s) in no Premium/Protection bucket left out: ' + skipped.join(', '));
            this._toastr.warning(`Unsupported Option Positions Were Left Out: ${skipped.join(', ')}`);
        }
        this._lastDesignFingerprint = '';
        this.replaceSheet(context, held);
        heldCalls.forEach(({position, option}) => {
            const row = this.sheet.addCallUnderlying(position.tradingInstrumentCode, option.underlying, option.strike,
                option.expiration, option.expirationTicker, position.netPosition, 'held', position.positionId);
            if (row) {
                this.subscribePrice(row.ticker);
                this.fetchBeta(option.underlying, row.ticker);
            }
        });
        const account = this.allCombos.find(c => c.comboId === comboId);
        this.sheet.setAccountValue(account && account.hasStartingCapital ? account.accountValue : null);
        if (account && account.hasStartingCapital && isFinite(account.freeCash)) {
            this.sheet.restoreAccountValuation({cash: account.freeCash, positions: mine.filter(p => p.netPosition > 0)
                .map(p => ({ticker: p.tradingInstrumentCode, quantity: p.netPosition, unitPrice: p.marketValue / p.netPosition}))});
        }
        this.publishSheet();
        console.info(`[pha] combo loaded: ${context.ownerUserName} / ${context.portfolioName} / ${context.comboName}: ${held.length} held name(s), `
            + `${options.length} held option(s)` + (shorts.length ? `, ${shorts.length} short left out` : '') + (byUser ? '' : ' (deep link)')
            + (this._analysis.sessionId ? ' — session ' + this._analysis.sessionId : ''));
        if (!held.length && !options.length && !heldCalls.length) {
            this._toastr.info('This combo holds no stock, ETF or option position');
        }
        await this.loadHeldOptions(comboId, options, sourceVersion);
        return this.selectedComboId === comboId && sourceVersion === this._sourceVersion;
    }

    /**
     * The combo's option positions as Premium / Protection rows (item 10): one row per position, on
     * the held underlying when the sheet has it (its contracts come out of that pool) else as an
     * index row; the chain is loaded, the position's expiration matched (same date, same option
     * root when listed), the strike and contracts set as held. A contract whose expiration is no
     * longer listed is retained with its contract facts when expired; an unexpired missing
     * listing is named so the trader can resolve it.
     */
    private async loadHeldOptions(comboId: string, options: PhaHeldOption[], sourceVersion = this._sourceVersion): Promise<void> {
        const unmatched: string[] = [];
        for (const o of options) {
            if (this.selectedComboId !== comboId || sourceVersion !== this._sourceVersion) {
                return; // the trader moved on while the chains were loading
            }
            if (o.section === 'premium') {
                const u = this.sheet.rows.find(r => r.ticker === o.underlying)
                    || this.sheet.rows.find(r => r.callUnderlying === o.underlying);
                const p = u ? this.sheet.addPremiumRow(u.id, o.optionType, o.side, 'held')
                    : this.sheet.addIndexPremiumRow(o.underlying, o.optionType, o.side, 'held');
                if (this._chains.isExpirationExpired(o.expirationDate)) {
                    this.restoreExpiredOption(p, o.underlying, o.expirationDate, o.expirationTicker, o.strike, o.contracts, o.optionTicker);
                    this.publishSheet();
                    continue;
                }
                const expirations = await this.loadChain(o.underlying, true);
                if (this.selectedComboId !== comboId || sourceVersion !== this._sourceVersion) { return; }
                this.sheet.setPremiumExpirations(o.underlying, expirations);
                const exp = findHeldExpiration(expirations, o.expirationDate, o.expirationTicker);
                if (exp) {
                    this.sheet.setPremiumExpiration(p.id, exp);
                    this.sheet.setPremiumStrike(p.id, o.strike);
                    // As the book holds it, never clamped by the pool (owner smoke 2026-09-30: a clamp
                    // here turned an over-covered 2-lot into 1 and manufactured a buy-back on import).
                    this.sheet.setPremiumHeldContracts(p.id, o.contracts);
                    const row = this.sheet.premiumRows.find(r => r.id === p.id);
                    if (row && row.contractsSelling > row.contractsAvailable) {
                        console.info(`[pha] held ${o.underlying} ${o.optionType} ${o.strike}: ${o.contracts} contract(s) held, over the pool (${row.contractsAvailable} available)`);
                    }
                    this.buildOptionTicker(row as PhaPremiumRow);
                } else {
                    unmatched.push(this.optionMessage(o.underlying, o.expirationDate, o.optionType, o.strike));
                }
            } else {
                const p = this.sheet.addProtectionRow(o.underlying, o.displayName, o.optionType, o.side, 'held');
                if (this._chains.isExpirationExpired(o.expirationDate)) {
                    this.restoreExpiredOption(p, o.underlying, o.expirationDate, o.expirationTicker, o.strike, o.contracts, o.optionTicker);
                    this.publishSheet();
                    continue;
                }
                const expirations = await this.loadChain(o.underlying, true);
                if (this.selectedComboId !== comboId || sourceVersion !== this._sourceVersion) { return; }
                this.sheet.setProtectionExpirations(o.underlying, expirations);
                const exp = findHeldExpiration(expirations, o.expirationDate, o.expirationTicker);
                if (exp) {
                    this.sheet.setProtectionExpiration(p.id, exp);
                    this.sheet.setProtectionStrike(p.id, o.strike);
                    this.sheet.setProtectionContracts(p.id, o.contracts);
                    this.buildProtectionTicker(this.sheet.protectionRows.find(r => r.id === p.id));
                } else {
                    unmatched.push(this.optionMessage(o.underlying, o.expirationDate, o.optionType, o.strike));
                }
            }
            this.publishSheet();
        }
        if (unmatched.length) {
            console.warn('[pha] held option(s) whose expiration is not listed: ' + unmatched.join(', '));
            this._toastr.warning(`Expiration no longer listed, pick one by hand: ${unmatched.join(', ')}`);
        }
        if (options.length) {
            console.info('[pha] held options loaded: ' + options.map(o => `${o.section}:${o.side} ${o.contracts} ${o.underlying} ${o.optionType} ${o.strike} ${o.expirationDate}`).join('; '));
        }
    }

    /**
     * RESTORE a saved sheet (CR-20260930-001 M2): the rows come back through the sheet's own API
     * (held rows as held, new rows as new, cross-outs, locks, the option rows with their picked
     * expiration re-matched on the listed chain). Prices, betas, chains and quotes come from the
     * feed again. `comboId` null = the homeless design.
     */
    private async applySheetState(context: PhaPortfolioContext | null, state: PhaSheetState, comboId: string | null,
                                  sourceVersion = this._sourceVersion): Promise<void> {
        const restoredOptions: Array<{row: PhaOptionRow, saved: PhaOptionRowState}> = [];
        const held: PhaHeldPosition[] = state.underlyings.filter(u => u.origin === 'held' && u.assetType !== 'Call')
            .map(u => ({ticker: u.ticker, displayName: this._displayNames?.getDisplayNameForTicker(u.ticker) || u.displayName,
                shares: u.shares, positionId: u.sourcePositionId, assetType: this.underlyingType(u.ticker, u.assetType)}));
        this.replaceSheet(context, held);
        this.sheet.contractValidationEnabled = false;
        try {
        const settings = state.sectionSettings;
        const premium = sectionSettings(settings && settings.premium);
        const protection = sectionSettings(settings && settings.protection);
        this.targetDte = {premium: premium.targetDte, protection: protection.targetDte};
        this.otmStep = {premium: premium.otmStep, protection: protection.otmStep};
        this.sheet.roundingDeviationPercent = state.roundingDeviationPercent === undefined ? 20 : state.roundingDeviationPercent;
        this.sheet.setStockAllocationPercent(state.stockAllocationPercent === undefined ? 100 : state.stockAllocationPercent);
        if (state.accountValuation || !comboId || !(this.sheet.accountValue > 0)) {
            this.sheet.setAccountValue(state.accountValue);
        }
        state.underlyings.forEach(u => {
            let row = this.sheet.rows.find(r => r.ticker === u.ticker);
            if (!row) {
                row = u.assetType === 'Call'
                    ? this.sheet.addCallUnderlying(u.ticker, u.callUnderlying, u.callStrike, u.callExpirationDate,
                        u.callExpirationTicker, u.shares, u.origin, u.sourcePositionId)
                    : this.sheet.addUnderlying(u.ticker, this._displayNames?.getDisplayNameForTicker(u.ticker) || u.displayName,
                        'new', u.shares, u.sourcePositionId, this.underlyingType(u.ticker, u.assetType));
                if (row) {
                    this.subscribePrice(u.ticker);
                    this.fetchBeta(u.callUnderlying || u.ticker, u.ticker);
                }
            }
            if (!row) {
                return;
            }
            if (row.assetType !== 'Call') {
                row.assetType = this.underlyingType(u.ticker, u.assetType);
                row.displayName = this._displayNames?.getDisplayNameForTicker(u.ticker) || u.displayName || u.ticker;
            }
            row.shares = Math.max(0, Math.floor(u.shares || 0));
            row.pctOverride = u.pctOverride === undefined ? null : u.pctOverride;
            row.sharesLock = !!u.sharesLock;
            row.pctLock = !!u.pctLock;
            row.side = row.assetType === 'Call' ? 'Buy' : u.side || 'Buy';
            if (u.crossedOut) {
                this.sheet.removeUnderlying(row.id);
            }
        });
        this.sheet.restoreAccountValuation(state.accountValuation, state.version < 11);
        this.sheet.recompute();
        this.publishSheet();

        const stillHere = () => this.selectedComboId === comboId && sourceVersion === this._sourceVersion;
        for (const s of state.premium) {
            if (!stillHere()) {
                return;
            }
            const u = s.pooled !== false ? this.sheet.rows.find(r => r.ticker === (s.pooledUnderlyingTicker || s.ticker)) : null;
            const p = u ? this.sheet.addPremiumRow(u.id, s.optionType, s.side, s.origin)
                : this.sheet.addIndexPremiumRow(s.ticker, s.optionType, s.side, s.origin);
            if (!p) {
                continue;
            }
            restoredOptions.push({row: p, saved: s});
            if (s.expirationDate && this._chains.isExpirationExpired(s.expirationDate)) {
                this.restoreExpiredOption(p, s.ticker, s.expirationDate, s.expirationTicker, s.strike, s.contracts, s.optionTicker);
                this.publishSheet();
                continue;
            }
            const expirations = await this.loadChain(s.ticker, true);
            if (!stillHere()) { return; }
            this.sheet.setPremiumExpirations(s.ticker, expirations);
            await this.restoreOptionRow(s, () => {
                const exp = s.expirationDate ? findHeldExpiration(expirations, s.expirationDate, s.expirationTicker) : null;
                if (exp) {
                    this.sheet.setPremiumExpiration(p.id, exp);
                    if (s.strike !== null && s.strike !== undefined) {
                        this.sheet.setPremiumStrike(p.id, s.strike);
                    }
                }
                // A held row's book count first (never clamped), then the designed count.
                if (s.heldContracts) {
                    this.sheet.setPremiumHeldContracts(p.id, s.heldContracts);
                }
                // Older saved designs may still call an acquired row "new". Restore its exact
                // desired count and recognise the book before the Premium pool can clamp it.
                this.buildOptionTicker(p);
                p.contractsSelling = s.contracts;
                this.sheet.reconcileOptionHoldings(optionHoldingsOf(this._analysis.snapshot, this._analysis.blocks),
                    optionContributionsOf(this.sheet, this._analysis.snapshot, this._analysis.blocks));
                this.sheet.setPremiumContracts(p.id, s.contracts);
                if (s.otmLock) {
                    this.sheet.togglePremiumOtmLock(p.id);
                }
                if (s.removed) {
                    this.sheet.removePremiumRow(p.id);
                }
                return !!exp || !s.expirationDate;
            });
        }
        for (const s of state.protection) {
            if (!stillHere()) {
                return;
            }
            const p = this.sheet.addProtectionRow(s.ticker, s.displayName, s.optionType, s.side, s.origin);
            restoredOptions.push({row: p, saved: s});
            if (s.expirationDate && this._chains.isExpirationExpired(s.expirationDate)) {
                this.restoreExpiredOption(p, s.ticker, s.expirationDate, s.expirationTicker, s.strike, s.contracts, s.optionTicker);
                this.publishSheet();
                continue;
            }
            const expirations = await this.loadChain(s.ticker, true);
            if (!stillHere()) { return; }
            this.sheet.setProtectionExpirations(s.ticker, expirations);
            await this.restoreOptionRow(s, () => {
                const exp = s.expirationDate ? findHeldExpiration(expirations, s.expirationDate, s.expirationTicker) : null;
                if (exp) {
                    this.sheet.setProtectionExpiration(p.id, exp);
                    if (s.strike !== null && s.strike !== undefined) {
                        this.sheet.setProtectionStrike(p.id, s.strike);
                    }
                }
                this.buildProtectionTicker(p);
                p.contracts = s.contracts;
                this.sheet.reconcileOptionHoldings(optionHoldingsOf(this._analysis.snapshot, this._analysis.blocks),
                    optionContributionsOf(this.sheet, this._analysis.snapshot, this._analysis.blocks));
                this.sheet.setProtectionContracts(p.id, s.contracts);
                if (s.otmLock) {
                    this.sheet.toggleProtectionOtmLock(p.id);
                }
                if (s.removed) {
                    this.sheet.removeProtectionRow(p.id);
                }
                this.buildProtectionTicker(this.sheet.protectionRows.find(r => r.id === p.id));
                return !!exp || !s.expirationDate;
            });
        }
        // Attach groups after row-by-row legacy restoration, so group setters do not re-edit
        // siblings while their chains are still loading. Restore ratios before the pool runs.
        restoredOptions.forEach(({row, saved}) => {
            if (!saved.strategyId && saved.strategyType !== 'Two-for-One') { return; }
            row.strategyId = saved.strategyId; row.strategyFamilyId = saved.strategyFamilyId || saved.strategyId;
            row.strategyType = saved.strategyType; row.strategyLeg = saved.strategyLeg;
            row.strategyUseAdjacent = saved.strategyUseAdjacent;
            row.strategyGap = saved.strategyGap; row.expectedStrike = saved.expectedStrike;
            if ('contractsSelling' in row) { row.contractsSelling = saved.contracts; }
            else { row.contracts = saved.contracts; }
            if (!this.sheet.isExpired(row) && row.expectedStrike !== undefined) {
                row.strike = row.expiration && row.expiration.strikes.indexOf(row.expectedStrike) >= 0 ? row.expectedStrike : null;
            } else if (!this.sheet.isExpired(row) && (row.strategyType === 'Two-for-One' || saved.strike == null)) {
                // Row-by-row legacy restore may resolve an OTM strike when a spot quote is already
                // cached. A saved blank is authoritative for every unconfigured strategy wing.
                row.expectedStrike = saved.strike == null ? undefined : saved.strike;
                row.strike = row.expiration && row.expiration.strikes.indexOf(saved.strike) >= 0 ? saved.strike : null;
            }
            if (!this.sheet.isExpired(row)) {
                if (row.strike === null) { row.optionTicker = null; }
                else if ('contractsSelling' in row) { this.buildOptionTicker(row); }
                else { this.buildProtectionTicker(row); }
            }
        });
        this.sheet.preserveRestoredTwoForOne();
        this.sheet.recompute();
        this.publishSheet();
        } finally { this.sheet.contractValidationEnabled = !this._restoreDepth; }
    }

    private isExpiredRow(id: number): boolean {
        const row = [...this.sheet.rows, ...this.sheet.premiumRows, ...this.sheet.protectionRows].find(r => r.id === id);
        return !!row && this.sheet.isExpired(row);
    }

    private restoreExpiredOption(row: PhaPremiumRow | PhaProtectionRow, underlying: string, date: string,
                                 root: string, strike: number, contracts: number, originalTicker?: string): void {
        const descriptor = {optionExpirationDate: date, expirationTicker: root, underlyingTicker: underlying, underlyingSymbol: underlying};
        const ticker = originalTicker || makeOptionTicker(descriptor as OptionExpirationDescriptor, optionTypeOf(row.optionType), strike);
        this.sheet.loadExpiredOption(row, {date, label: 'Expired', strikes: [], descriptor}, strike, ticker, contracts);
    }

    private _settlementRequests: {[ticker: string]: Promise<void>} = {};

    private optionMessage(symbol: string, date: string, type?: string, strike?: number): string {
        return optionMessage(this._displayNames.getDisplayNameForTicker(symbol) || this.sheet.displayNameForSymbol(symbol), date, type, strike);
    }

    private reconcileSettlementPrices(): void {
        const rows = [...this.sheet.rows, ...this.sheet.premiumRows, ...this.sheet.protectionRows];
        rows.filter(r => this.sheet.isExpired(r)).forEach(row => {
            const ticker = 'assetType' in row ? row.ticker : row.optionTicker;
            if (this._settlementRequests[ticker]) { return; }
            const option = parseOptionTicker(ticker);
            if (!option) { return; }
            const requests = this._settlementRequests;
            this.sheet.setSettlementPrice(ticker, null);
            requests[ticker] = this._settlementPrices.getSettlementPriceWithRequest(option.underlying, option.expiration)
                .then(price => {
                    if (requests !== this._settlementRequests || this._unsub.isStopped) { return; }
                    const value = finiteOrNull(price);
                    this.sheet.setSettlementPrice(ticker, value);
                    if (value === null) { this._toastr.warning(`Settlement Price Is Unavailable for ${this.optionMessage(option.underlying, option.expiration)}`); }
                    this.publishSheet();
                }).catch(error => {
                    if (requests !== this._settlementRequests || this._unsub.isStopped) { return; }
                    console.error('[pha] settlement lookup failed', ticker, error);
                    this._toastr.warning(`Settlement Price Could Not Be Loaded for ${this.optionMessage(option.underlying, option.expiration)}`);
                    this.publishSheet();
                });
        });
    }

    private async restoreOptionRow(s: PhaOptionRowState, apply: () => boolean): Promise<void> {
        const matched = apply();
        if (!matched) {
            console.warn('[pha] restored ' + s.ticker + ' ' + s.optionType + ' ' + s.strike + ' ' + s.expirationDate + ': expiration no longer listed');
            this._toastr.warning(`Expiration no longer listed, pick one by hand: ${this.optionMessage(s.ticker, s.expirationDate, s.optionType, s.strike)}`);
        }
        this.publishSheet();
    }

    /** A new analysis on the given context: every subscription of the old one is released first. */
    private replaceSheet(context: PhaPortfolioContext | null, held: PhaHeldPosition[]): void {
        this.cancelOptimization();
        this.sheet.roundingDeviationPercent = 20;
        this.cancelOtmMovers();
        this.targetDte = {premium: 14, protection: 14};
        this.otmStep = {premium: 1, protection: 1};
        this.sheet.rows.forEach(r => this.unsubscribePrice(r.ticker));
        this._settlementRequests = {};
        this.sheet.sessionLockedRowIds = {};
        this.sheet.loadPortfolio(context, held);
        this.sheet.rows.forEach(r => {
            this.subscribePrice(r.ticker);
            this.fetchBeta(r.ticker);
        });
        this.publishSheet();
    }

    // ---- the deep link from a Collars portfolio row (M5) --------------------------------------
    // Live: the host stamps the ONE panel that takes it. Boot: a fresh request cached on the bus is
    // taken by a panel added for it (the Hedging Grid pattern). The portfolio list may still be
    // loading, so the selection retries a few times.
    private takesSelectRequest(request: OpenSubjectRequest): boolean {
        return !!request && request.panel === 'PortfolioHedgingAnalysis'
            && (!request.targetPanelId || request.targetPanelId === this.panelId);
    }

    private async applySelectRequest(request: OpenSubjectRequest, retries: number): Promise<void> {
        for (let i = 0; i <= retries; i++) {
            // The list is built once; a combo created or freed after that (a new home, a Shell restart
            // while the panel was open) is not in it — a miss re-reads the list before giving up
            // (owner smoke 2026-09-30: "the requested combo is not available to you" on a combo that is).
            if (!this.portfolioGroups.length || i > 0) {
                await this.loadPortfolios();
            }
            const combo = request.comboId ? this.allCombos.find(c => c.comboId === request.comboId) : null;
            if (combo) {
                // Both selectors follow the link: the combo's portfolio first, then the combo itself.
                const portfolios: PhaPortfolioChoice[] = [];
                this.portfolioGroups.forEach(g => portfolios.push(...g.items));
                this.selectedPortfolio = portfolios.find(p => p.portfolioId === combo.portfolioId) || null;
                this.combos = this.selectedPortfolio ? this.selectedPortfolio.combos.slice() : [];
                await this.selectCombo(combo.comboId, false);
                return;
            }
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        console.warn('[pha] deep link: combo ' + request.comboId + ' is not in the list');
        this._toastr.warning('The Requested Combo Is Not Available to You');
    }

    // ---- the balance buttons (M2) -------------------------------------------------------------
    // Three separate buttons, one click each. No pressed state on the button: a success toast
    // reports the completed move (owner, 2026-09-29 vrequest). Every move is all-or-nothing in the
    // sheet; a refusal is shown with the reason and the row it names.
    readonly balanceButtons: Array<{id: PhaBalanceMode, text: string, done: string}> = [
        {id: 'percent', text: 'Auto-Balance %', done: 'Auto-balance by portfolio share completed'},
        {id: 'beta', text: 'Beta Optimization', done: 'Beta optimization completed'},
        {id: 'rounding', text: 'Rounding Optimization', done: 'Rounding optimization completed'}
    ];

    optimizing = false;
    roundingReport: PhaRoundingReport | null = null;
    roundingReportVisible = false;
    private optimizationUndo: OptimizationUndoCheckpoint | null = null;
    private optimizationRun = 0;
    private optimizationDestroyed = false;

    private optimizationFingerprint(): string {
        const state = serializeSheet(this.sheet, {premium: {targetDte: this.targetDte && this.targetDte.premium,
            otmStep: this.otmStep && this.otmStep.premium}, protection: {targetDte: this.targetDte && this.targetDte.protection,
            otmStep: this.otmStep && this.otmStep.protection}});
        delete state.accountValuation;
        return JSON.stringify(state);
    }

    private optimizationBrokerFingerprint(): string {
        const analysis = this._analysis;
        return JSON.stringify([analysis && analysis.sessionId, analysis && analysis.snapshot,
            analysis && (analysis.blocks || []).filter(b => b.status !== 'Pending')]);
    }

    private checkOptimizationUndo(): void {
        if (this.optimizationUndo && (this.optimizationUndo.afterFingerprint !== this.optimizationFingerprint()
            || this.optimizationUndo.brokerFingerprint !== this.optimizationBrokerFingerprint())) {
            this.optimizationUndo = null;
        }
    }

    get canUndoOptimization(): boolean {
        return !this.optimizing && !!this.optimizationUndo
            && this.optimizationUndo.afterFingerprint === this.optimizationFingerprint()
            && this.optimizationUndo.brokerFingerprint === this.optimizationBrokerFingerprint();
    }

    get optimizationUndoHint(): string {
        return this.canUndoOptimization ? 'Undo ' + this.optimizationUndo.label : 'Undo the latest optimization';
    }

    @DetectMethodChanges()
    onRoundingDeviationChanged(ev: {value: number, component?: any}): void {
        if (this.optimizing || this._restoreDepth) { return; }
        const value = ev && ev.value;
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 100) {
            if (ev && ev.component) { ev.component.option('value', this.sheet.roundingDeviationPercent); }
            return;
        }
        const rounded = Math.round(value * 100) / 100;
        if (rounded === this.sheet.roundingDeviationPercent) { return; }
        this.sheet.roundingDeviationPercent = rounded;
        this.publishSheet();
    }

    private cancelOptimization(): void {
        this.optimizationRun = (this.optimizationRun || 0) + 1;
        this.optimizing = false; this.optimizationUndo = null;
        this.roundingReportVisible = false; this.roundingReport = null;
    }

    protected paintOptimizationBusy(): Promise<void> {
        this._changeDetector.detectChanges();
        return new Promise(resolve => {
            if (typeof requestAnimationFrame === 'function') { requestAnimationFrame(() => setTimeout(resolve, 0)); }
            else { setTimeout(resolve, 0); }
        });
    }

    @DetectMethodChanges()
    onOptimizationUndoClicked(): void {
        this.checkOptimizationUndo();
        if (!this.canUndoOptimization || !this.isUnlocked) { return; }
        const checkpoint = this.optimizationUndo;
        const result = this.sheet.restoreOptimization(checkpoint.before);
        this.optimizationUndo = null;
        if (!result.applied) { this._toastr.warning(result.reason); return; }
        this.roundingReport = checkpoint.previousReport; this.roundingReportVisible = false;
        this.publishSheet(); this._toastr.success(checkpoint.label + ' undone');
    }

    @DetectMethodChanges()
    onRoundingReportClosed(): void { this.roundingReportVisible = false; }

    @DetectMethodChanges()
    async onBalanceClicked(mode: PhaBalanceMode): Promise<void> {
        if (!this.isUnlocked || this.optimizing || this.resetting || this.panelLoading || this.optimizationDestroyed) { return; }
        this.checkOptimizationUndo();
        const run = this.optimizationRun = (this.optimizationRun || 0) + 1;
        const source = this._sourceVersion;
        const broker = this.optimizationBrokerFingerprint();
        const before = this.sheet.captureOptimization();
        const fingerprint = this.optimizationFingerprint();
        const input = mode === 'rounding' ? this.sheet.roundingInput() : null;
        const previousReport = this.roundingReport;
        const cancelled = () => this.optimizationDestroyed || run !== this.optimizationRun
            || source !== this._sourceVersion || broker !== this.optimizationBrokerFingerprint();
        this.optimizing = true; this.roundingReportVisible = false;
        try {
            await this.paintOptimizationBusy();
            if (cancelled() || fingerprint !== this.optimizationFingerprint()) { return; }
            let result: PhaBalanceResult;
            let report: PhaRoundingReport = null;
            if (mode === 'rounding') {
                const calculated = await calculateRounding(input, cancelled);
                if (cancelled()) { return; }
                if (fingerprint !== this.optimizationFingerprint()) {
                    this._toastr.info('The analysis changed during calculation. Please calculate again.'); return;
                }
                report = calculated.report;
                result = calculated.status === 'optimal' ? this.sheet.applyRounding(report)
                    : {applied: false, reason: calculated.reason};
            } else { result = mode === 'percent' ? this.sheet.balanceByPercent() : this.sheet.balanceByBeta(); }
            const changed = fingerprint !== this.optimizationFingerprint();
            this.publishSheet();
            if (result.applied && changed) {
                const button = this.balanceButtons.find(b => b.id === mode);
                this.optimizationUndo = {before, afterFingerprint: this.optimizationFingerprint(),
                    brokerFingerprint: this.optimizationBrokerFingerprint(), label: button.text, previousReport};
                this.roundingReport = report; this.roundingReportVisible = !!report;
                this._toastr.success(button.done);
            } else { this._toastr.info(result.reason || 'The optimization did not change the design.'); }
        } catch (e) {
            this._toastr.error('Optimization failed; please try again.');
            console.error('[pha] optimization failed', e);
        } finally {
            if (run === this.optimizationRun) { this.optimizing = false; }
            if (!this.optimizationDestroyed) { this._changeDetector.detectChanges(); }
        }
    }

    // ---- panel lifecycle ---------------------------------------------------------------------
    etsOnInit(): void {
        this._instruments?.newInstrument.pipe(takeUntil(this._unsub)).subscribe(instrument => {
            if (!this.sheet.rows.some(row => row.ticker === instrument.ticker)
                && ![...this.sheet.premiumRows, ...this.sheet.protectionRows].some(row => row.optionTicker === instrument.ticker)) { return; }
            this.sheet.recompute();
            this.publishSheet();
        });
        this._messageBus
            .of<QuoteDto[]>('QuoteDto')
            .pipe(takeUntil(this._unsub))
            .subscribe(msg => this.onQuotes(msg.payload));

        this._messageBus
            .of<OpenSubjectRequest>(PHA_SELECT_PORTFOLIO_REQUEST_TOPIC)
            .pipe(takeUntil(this._unsub))
            .subscribe(x => {
                if (this.takesSelectRequest(x.payload)) {
                    this.applySelectRequest(x.payload, 1);   // one re-read of the list on a miss
                }
            });

        this._analysis.sessionChanged$
            .pipe(takeUntil(this._unsub))
            .subscribe(dto => this.onSessionChanged(dto));

        // CR-20260930-001 M3: the last block acquired in Portfolio Hedging FINISHES the session (the combo
        // is free again). The design is done: say so and re-import the combo as it is now, into a new
        // session — the same road as after a Reset. An abort pushed from elsewhere changes nothing here.
        this._analysis.sessionEnded$
            .pipe(takeUntil(this._unsub))
            .subscribe(ended => this.onSessionEnded(ended));

        const sourceVersion = this._sourceVersion;
        this.loadPortfolios().then(() => {
            // Panel reopen / login with no combo picked: a homeless design comes back on its own.
            if (sourceVersion === this._sourceVersion
                && !this.selectedComboId && !this._analysis.isLive) {
                this.restoreHomelessIfAny();
            }
        });
        this._staleTimer = setInterval(() => this.checkStalePrices(), STALE_CHECK_MS);
    }

    etsOnDestroy(): void {
        this.benchmarkDialog?.close();
        this.optimizationDestroyed = true;
        this.cancelOptimization();
        if (this._staleTimer) {
            clearInterval(this._staleTimer);
            this._staleTimer = null;
        }
        this.cancelOtmMovers();
        // The design in flight goes out before the panel dies (§5: saved the moment it changes).
        this._analysis.flushReplace();
        this._unsub.next();
        this._unsub.complete();
        // The panel dies; the SESSION lives on in the Shell. Release every price subscription it took.
        this.sheet.rows.forEach(r => this.unsubscribePrice(r.ticker));
        Object.keys(this._optionSubscriptions).forEach(t => this._lastQuoteCache.unsubscribeTicker(t));
        this._optionSubscriptions = {};
        Object.keys(this._spotSubscriptions).forEach(t => this._lastQuoteCache.unsubscribeTicker(t));
        this._spotSubscriptions = {};
    }

    /** Portfolio Hedges can change starting capital while this panel is hidden. Re-read on return. */
    protected onIsActiveChanged(): void {
        if (this.isActive) {
            this.loadPortfolios();
        }
    }

    etsAfterViewInit(): void {
        const pending = this._messageBus.getLastMessage<OpenSubjectRequest>(PHA_SELECT_PORTFOLIO_REQUEST_TOPIC);
        if (pending && pending.payload && Date.now() - pending.payload.at < SELECT_REQUEST_TTL_MS
            && this.takesSelectRequest(pending.payload)) {
            this._messageBus.clearLastMessage(PHA_SELECT_PORTFOLIO_REQUEST_TOPIC);
            setTimeout(() => this.applySelectRequest(pending.payload, 8), 300);
        }
    }

    // The panel's own user setting stays empty: the SESSION in the Shell is the record (keyed by combo).
    protected getState(): any {
        return null;
    }

    protected setState(state: any) {
    }
}

/** One client's portfolios: the `{key, items}` shape DevExtreme's grouped select-box takes. */
interface PhaPortfolioGroup {
    key: string;
    isOwn: boolean;
    items: PhaPortfolioChoice[];
}

interface PhaPortfolioChoice {
    portfolioId: string;
    name: string;
    ownerTerminalId: string;
    ownerUserName: string;
    combos: PhaComboChoice[];
}

interface PhaComboChoice {
    comboId: string;
    name: string;
    portfolioId: string;
    portfolioName: string;
    ownerUserName: string;
    ownerTerminalId: string;
    hasStartingCapital: boolean;
    startingCapital: number | null;
    freeCash: number;
    accountValue: number;
}

function sectionKeyOf(section: PhaSection): AnalysisSection {
    return section === 'underlyings' ? 'Underlyings' : (section === 'premium' ? 'Premium' : 'Protection');
}

/** "BUY 100 AAPL" / "SELL 3 AAPL Call 240 17 OCT 26": a change as the trader reads it. */
function describeChange(c: PhaChange): string {
    return (c.side > 0 ? 'BUY ' : 'SELL ') + c.qty + ' ' + describeInstrument(c);
}

function describeInstrument(c: PhaChange): string {
    if (c.kind === 0) {
        return c.tradingInstrumentCode;
    }
    return `${c.underlying} ${c.optionType} ${c.strike} ${tosDate(c.expiration)}`;
}

/** The feed's timestamp when it carries one, else now. */
function quoteInstant(q: QuoteDto): number {
    const ts: any = q && q.timeStamp;
    if (ts instanceof Date && !isNaN(ts.getTime())) {
        return ts.getTime();
    }
    if (typeof ts === 'string') {
        const parsed = Date.parse(ts);
        if (!isNaN(parsed)) {
            return parsed;
        }
    }
    return Date.now();
}

function finiteOrNull(v: number): number | null {
    return typeof v === 'number' && isFinite(v) ? v : null;
}

/** The sheet's option type as the platform's enum, for makeOptionTicker. */
function optionTypeOf(t: PhaOptionType): OptionType {
    return t === 'Put' ? OptionType.Put : OptionType.Call;
}

/**
 * The listed expiration a held (or saved) contract sits on: same date AND same option root (SPX vs
 * SPXW) when such a listing exists, else the same date alone.
 */
function findHeldExpiration(expirations: PhaExpiration[], date: string, expirationTicker: string | null): PhaExpiration | null {
    const sameDate = (expirations || []).filter(e => e.date === date);
    if (!sameDate.length) {
        return null;
    }
    const sameRoot = expirationTicker ? sameDate.find(e => e.descriptor && e.descriptor.expirationTicker === expirationTicker) : null;
    return sameRoot || sameDate[0];
}

function pickPrice(q: QuoteDto): number | null {
    if (!q) {
        return null;
    }
    if (isFinite(q.lastPx) && q.lastPx > 0) {
        return q.lastPx;
    }
    if (isFinite(q.mid) && q.mid > 0) {
        return q.mid;
    }
    return null;
}
